import {
  Fragment,
  StrictMode,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { createRoot } from "react-dom/client";
import { Brand } from "./components/brand.tsx";
import { GuestEntryScreen } from "./components/guest-entry-screen.tsx";
import { PendingInvitationsSection } from "./components/pending-invitations.tsx";
import {
  invitationPath,
  invitationsPath,
  inviteRefusal,
  pendingInvitationFor,
  readPendingInvitations,
  withSentInvitation,
  type InvitationInventory,
  type PendingInvitation,
} from "./pending-invitations.ts";
import { GuestEntry } from "./guest-entry.ts";
import { parseLogoutRedirect } from "./logout";
import { selectOrganizationSession } from "./organization-selection.ts";
import {
  Alert02Icon,
  Archive01Icon,
  Edit02Icon,
  Book02Icon,
  Comment01Icon,
  ComputerTerminal01Icon,
  Delete02Icon,
  ExternalLinkIcon,
  LinkSquare01Icon,
  MoreHorizontalIcon,
  Plug01Icon,
  Logout01Icon,
  Moon02Icon,
  PackageAddIcon,
  Sun03Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";

import {
  AgentSetupDialog,
  type AgentSetupDialogKind,
} from "@/components/agent-setup";
import {
  ConsoleSection,
  PageHeader,
  SecretPanel,
  StatePanel,
  StatusBadge,
} from "@/components/product";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Avatar } from "@/components/ui/avatar";
import {
  AccessIcon,
  BackIcon,
  BillingIcon,
  CheckIcon,
  ChevronDownIcon,
  ExternalIcon,
  MoreIcon,
  NotesIcon,
  PlusIcon,
  SendIcon,
  SitesIcon,
  TeamIcon,
  UpDownIcon,
} from "@/components/icons";
import { firstName, initial, slotOf } from "@/lib/people";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Field as FieldRoot,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import {
  SiteConnectedView,
  SiteInstallView,
  useInstallationCheck,
} from "@/components/site-setup";
import {
  inkButton,
  inkButtonLarge,
  QuietHistory,
  QuietLine,
  QuietLoading,
  QuietRow,
  QuietRows,
  QuietSection,
} from "@/components/quiet";
import { agentConnectionPrompt } from "@/lib/agent-connection-prompt";
import { setupStatus } from "./installation-check.ts";
import {
  apiKeyRows,
  guestLinkHistorySummary,
  guestLinkRows,
  sinceLabel,
  usedLabel,
  type GuestLinkRow,
} from "./access-rows.ts";
import { exactTime, relativeTime } from "./relative-time.ts";
import {
  AuthorAvatar,
  AuthorKind,
  NoteListRow,
  NoteStateTabs,
  SiteMenu,
  type NoteStateFilter,
} from "@/components/notes";
import { NoteSubjectImage } from "@/components/note-image";
import { parseNoteImage } from "./note-image.ts";
import {
  authorView,
  matchesPage,
  openNoteTotal,
  pagePath,
  screenshotViewport,
  stateCounts,
  type PageFilter,
} from "./note-view.ts";
import { Input } from "@/components/ui/input";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  ApiError,
  type ApiKey,
  type BillingSummary,
  type GuestLink,
  type Me,
  type Membership,
  type MembershipManagement,
  type Note,
  type Reply,
  type Site,
  type TeamMember,
} from "./api.ts";
import "./index.css";
import {
  canAdministerMember,
  canAdministerWorkspace,
  canManageMembers,
  canModifyAuthoredContent,
  canReplyToNote,
} from "./permissions.ts";
import { launchSiteReview } from "./site-launch.ts";
import { createSelfReview, hasLaunchFragment } from "./staging-review.ts";
import {
  usePendingActions,
  useRetryOperationIds,
  type PendingActions,
} from "./pending-actions.ts";
import { OrganizationResponseGuard } from "./organization-response-guard.ts";
import { useScopedApi } from "./use-scoped-api.ts";
import { useTheme } from "./use-theme.ts";
import { ConnectedAgents } from "./components/connected-agents.tsx";
import {
  createBrowserCredentialRecoveryStore,
  credentialRecoveryNeedsSaving,
  type CredentialRecovery,
  type GuestLinkCredentialRecovery,
} from "./credential-recovery.ts";
import {
  copyGuestLinkFromReceipt,
  credentialRecoveryCannotContinue,
  issueGuestLinkFromReceipt,
} from "./guest-link-copy.ts";

type Section = "sites" | "notes" | "access" | "team" | "billing";

const documentationUrl = "https://appnotes.dev/docs";

type RevealedCredential = {
  readonly label: string;
  readonly recovery: CredentialRecovery;
  readonly value: string;
};

type Inventory<T> =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly items: readonly T[] }
  | { readonly status: "error"; readonly message: string };

function App() {
  const api = useScopedApi();
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const { dark, toggle } = useTheme();
  const meRequest = useRef<AbortController | null>(null);

  async function loadMe(): Promise<void> {
    meRequest.current?.abort();
    const controller = new AbortController();
    meRequest.current = controller;
    setError(null);
    setMe(undefined);
    try {
      setMe(await api<Me>("/v1/me", { signal: controller.signal }));
      setError(null);
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === "request_cancelled")
        return;
      if (cause instanceof ApiError && cause.status === 401) setMe(null);
      else setError(errorMessage(cause));
    }
  }

  useEffect(() => void loadMe(), []);
  if (error !== null) {
    return <FatalScreen message={error} retry={() => void loadMe()} />;
  }
  if (me === undefined) return <LoadingScreen />;
  if (me === null) return <SignInScreen />;
  if (me.selected_organization_id === null) {
    return (
      <OrganizationGate me={me} onChange={setMe} onSessionError={setError} />
    );
  }
  const membership = me.memberships.find(
    (candidate) => candidate.organization_id === me.selected_organization_id,
  );
  if (membership === undefined) {
    return (
      <FatalScreen
        message="Your selected team is no longer available."
        retry={() => void loadMe()}
      />
    );
  }
  return (
    <Workspace
      dark={dark}
      me={me}
      membership={membership}
      onMeChange={setMe}
      onSessionError={setError}
      onThemeChange={toggle}
    />
  );
}

function Workspace({
  dark,
  me,
  membership,
  onMeChange,
  onSessionError,
  onThemeChange,
}: {
  readonly dark: boolean;
  readonly me: Me;
  readonly membership: Membership;
  readonly onMeChange: (me: Me | null) => void;
  readonly onSessionError: (message: string) => void;
  readonly onThemeChange: () => void;
}) {
  const api = useScopedApi();
  const [sessionError, setSessionError] = useState<string | null>(null);
  // Notes is home when the team has a site; Sites is home on first run (or
  // when the Site list cannot be read). Chosen once, on the first Site read.
  const [section, setSection] = useState<Section | null>(null);
  const [accessSiteId, setAccessSiteId] = useState<string | null>(null);
  // A Sites row for a connected site opens Notes filtered to it.
  const [notesSiteId, setNotesSiteId] = useState<string | null>(null);
  // Choosing a section again from the sidebar starts it over (Sites returns
  // from its add and install views to the list).
  const [sectionVisit, setSectionVisit] = useState(0);
  const [agentSetup, setAgentSetup] = useState<AgentSetupDialogKind | null>(
    null,
  );
  const [revision, setRevision] = useState(0);
  const [switchingTeam, setSwitchingTeam] = useState(false);
  const [shellSites, setShellSites] = useState<readonly Site[] | null>(null);
  const [shellSitesFailed, setShellSitesFailed] = useState(false);
  const pending = usePendingActions();
  const refresh = () => setRevision((value) => value + 1);

  // The shell reads the Site list once per change for the Notes count and the
  // Notes Site filter; a failed read only hides the count.
  useEffect(() => {
    const controller = new AbortController();
    setShellSites(null);
    setShellSitesFailed(false);
    api<{ sites: Site[] }>(
      `/v1/orgs/${membership.organization_id}/appnotes/sites?limit=100`,
      { signal: controller.signal },
    )
      .then((value) => setShellSites(value.sites))
      .catch((cause: unknown) => {
        if (!requestCanceled(cause)) setShellSitesFailed(true);
      });
    return () => controller.abort();
  }, [membership.organization_id, revision]);

  useEffect(() => {
    if (section !== null) return;
    if (shellSites !== null)
      setSection(shellSites.length > 0 ? "notes" : "sites");
    else if (shellSitesFailed) setSection("sites");
  }, [section, shellSites, shellSitesFailed]);

  function openAccess(siteId: string | null): void {
    setAccessSiteId(siteId);
    setSection("access");
  }

  function openSection(next: Section): void {
    if (next === "notes") setNotesSiteId(null);
    if (next === "access") setAccessSiteId(null);
    setSectionVisit((value) => value + 1);
    setSection(next);
  }

  function openNotes(siteId: string): void {
    setNotesSiteId(siteId);
    setSectionVisit((value) => value + 1);
    setSection("notes");
  }

  useEffect(() => {
    setAccessSiteId(null);
    setNotesSiteId(null);
  }, [membership.organization_id]);

  async function selectOrganization(organizationId: string): Promise<void> {
    await pending.run("session:navigation", async () => {
      setSessionError(null);
      setSwitchingTeam(true);
      try {
        const result = await selectOrganizationSession(
          api,
          organizationId,
          me.csrf_token,
        );
        switch (result.status) {
          case "confirmed":
            onMeChange(result.me);
            setSessionError(result.notice);
            break;
          case "signed_out":
            onMeChange(null);
            break;
          case "unavailable":
            onSessionError(result.message);
            break;
          case "cancelled":
            break;
        }
      } finally {
        setSwitchingTeam(false);
      }
    });
  }

  async function logout(): Promise<void> {
    await pending.run("session:navigation", async () => {
      setSessionError(null);
      try {
        const response = await api<unknown>("/auth/logout", {
          method: "POST",
          csrf: me.csrf_token,
        });
        const target = parseLogoutRedirect(response, window.location.origin);
        if (target === null) {
          setSessionError(
            "AppNotes returned an invalid sign-out response. Reload to check your session before trying again.",
          );
          return;
        }
        window.location.assign(target.href);
      } catch (cause) {
        setSessionError(`Could not confirm sign out. ${errorMessage(cause)}`);
      }
    });
  }

  const busy = pending.isPending("session:navigation");
  const accountMenu = (
    <AccountMenuContent
      busy={busy}
      dark={dark}
      membership={membership}
      name={me.user.display_name}
      onAgentSetup={setAgentSetup}
      onLogout={() => void logout()}
      onThemeChange={onThemeChange}
    />
  );
  return (
    <div className="workspace-shell">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <aside aria-label="AppNotes" className="workspace-sidebar">
        <div className="workspace-sidebar-brand">
          <Brand />
        </div>
        <div className="workspace-mobile-controls">
          <span className="workspace-mobile-team">
            <NativeSelect
              aria-label="Current team"
              disabled={busy}
              value={membership.organization_id}
              onChange={(event) => void selectOrganization(event.target.value)}
            >
              {me.memberships.map((item) => (
                <NativeSelectOption
                  key={item.organization_id}
                  value={item.organization_id}
                >
                  {item.organization_name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  aria-label="Open account menu"
                  size="icon"
                  type="button"
                  variant="ghost"
                />
              }
            >
              <AvatarForUser id={me.user.id} name={me.user.display_name} />
            </DropdownMenuTrigger>
            {accountMenu}
          </DropdownMenu>
        </div>
        <TeamSwitcher
          current={membership}
          disabled={busy}
          memberships={me.memberships}
          switching={switchingTeam}
          onSelect={(organizationId) => void selectOrganization(organizationId)}
        />
        <nav
          aria-label="AppNotes sections"
          className="workspace-nav"
          data-switching={switchingTeam}
          inert={switchingTeam}
        >
          <NavButton
            active={section === "notes"}
            count={shellSites === null ? null : openNoteTotal(shellSites)}
            icon={NotesIcon}
            onClick={() => openSection("notes")}
          >
            Notes
          </NavButton>
          <NavButton
            active={section === "sites"}
            icon={SitesIcon}
            onClick={() => openSection("sites")}
          >
            Sites
          </NavButton>
          <NavButton
            active={section === "access"}
            icon={AccessIcon}
            onClick={() => openSection("access")}
          >
            Access
          </NavButton>
          <NavButton
            active={section === "team"}
            icon={TeamIcon}
            onClick={() => openSection("team")}
          >
            Team
          </NavButton>
          {me.billing_available ? (
            <NavButton
              active={section === "billing"}
              icon={BillingIcon}
              onClick={() => openSection("billing")}
            >
              Billing
            </NavButton>
          ) : null}
        </nav>
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label={`Account: ${me.user.display_name}`}
            className="workspace-account"
          >
            <AvatarForUser id={me.user.id} name={me.user.display_name} />
            <span className="truncate">{me.user.display_name}</span>
          </DropdownMenuTrigger>
          {accountMenu}
        </DropdownMenu>
      </aside>
      <AgentSetupDialog
        apiOrigin={window.location.origin}
        mcpAvailable={me.mcp_available === true}
        kind={agentSetup}
        membership={membership}
        onOpenChange={(open) => {
          if (!open) setAgentSetup(null);
        }}
      />
      <main className="workspace-content" id="main-content" tabIndex={-1}>
        {sessionError !== null || busy ? (
          <div className="workspace-notices">
            {sessionError !== null && <Notice>{sessionError}</Notice>}
            {busy && <Notice>Confirming your session…</Notice>}
          </div>
        ) : null}
        <div className="workspace-page" inert={busy}>
          <Fragment key={membership.organization_id}>
            {section === null ? (
              <p className="loading-label page" role="status">
                Loading…
              </p>
            ) : section === "sites" ? (
              <SitesPage
                key={sectionVisit}
                me={me}
                membership={membership}
                revision={revision}
                onChange={refresh}
                onOpenAccess={(siteId) => openAccess(siteId)}
                onOpenNotes={openNotes}
              />
            ) : section === "notes" ? (
              <NotesPage
                initialSiteId={notesSiteId}
                key={sectionVisit}
                me={me}
                membership={membership}
                revision={revision}
                onChange={refresh}
                sites={shellSites}
              />
            ) : section === "access" ? (
              <AccessPage
                focusedSiteId={accessSiteId}
                me={me}
                membership={membership}
                revision={revision}
                onChange={refresh}
                onOpenAgentInstructions={() => setAgentSetup("instructions")}
              />
            ) : section === "team" ? (
              <TeamPage me={me} membership={membership} onChange={refresh} />
            ) : (
              <BillingPage me={me} membership={membership} />
            )}
          </Fragment>
        </div>
      </main>
    </div>
  );
}

type SitesView =
  | { readonly kind: "list" }
  | { readonly kind: "add" }
  | { readonly kind: "setup"; readonly siteId: string };

function SitesPage({
  me,
  membership,
  revision,
  onChange,
  onOpenAccess,
  onOpenNotes,
}: PageProps & {
  readonly onOpenAccess: (siteId: string) => void;
  readonly onOpenNotes: (siteId: string) => void;
}) {
  const api = useScopedApi();
  const [sites, setSites] = useState<readonly Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [view, setView] = useState<SitesView>({ kind: "list" });
  const [addError, setAddError] = useState<string | null>(null);
  const [createdSite, setCreatedSite] = useState<Site | null>(null);
  const [pendingDeletion, setPendingDeletion] = useState<Site | null>(null);
  const [launchingSiteId, setLaunchingSiteId] = useState<string | null>(null);
  const pending = usePendingActions();
  const administrator = canAdministerWorkspace(membership.role);
  const installation = useInstallationCheck({
    csrf: me.csrf_token,
    request: api,
    onSite: (site) =>
      setSites((current) =>
        current.map((candidate) =>
          candidate.id === site.id ? site : candidate,
        ),
      ),
  });
  const apiOrigin = window.location.origin;

  useEffect(() => {
    let current = true;
    const controller = new AbortController();
    setLoading(true);
    api<{ sites: Site[] }>(
      `/v1/orgs/${membership.organization_id}/appnotes/sites?limit=100`,
      { signal: controller.signal },
    )
      .then((value) => {
        if (!current) return;
        setSites(value.sites);
        setMessage(null);
      })
      .catch((cause) => current && setMessage(errorMessage(cause)))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
      controller.abort();
    };
  }, [membership.organization_id, revision]);

  async function createSite(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    await pending.run("site:create", async () => {
      setAddError(null);
      const primaryUrl = String(form.get("primary_url") ?? "");
      try {
        const created = await api<Site>(
          `/v1/orgs/${membership.organization_id}/appnotes/sites`,
          {
            method: "POST",
            csrf: me.csrf_token,
            body: {
              name: form.get("name"),
              origin: new URL(primaryUrl).origin,
              primary_url: primaryUrl,
            },
          },
        );
        formElement.reset();
        openSetup(created);
        onChange();
      } catch (cause) {
        if (
          cause instanceof ApiError &&
          cause.code === "appnotes_site_origin_conflict"
        ) {
          const existing = await api<{ sites: Site[] }>(
            `/v1/orgs/${membership.organization_id}/appnotes/sites?limit=100`,
          ).catch(() => null);
          const site = existing?.sites.find(
            (candidate) => candidate.origin === new URL(primaryUrl).origin,
          );
          if (site !== undefined) {
            openSetup(site);
            onChange();
            return;
          }
        }
        setAddError(errorMessage(cause));
      }
    });
  }

  function openSetup(site: Site): void {
    installation.reset();
    setCreatedSite(site);
    setAddError(null);
    setView({ kind: "setup", siteId: site.id });
  }

  function backToList(): void {
    installation.reset();
    setCreatedSite(null);
    setAddError(null);
    setView({ kind: "list" });
  }

  function openSite(site: Site): void {
    if ((site.last_connected_at ?? null) === null) openSetup(site);
    else onOpenNotes(site.id);
  }

  async function updateSite(
    site: Site,
    body: { readonly enabled: boolean },
  ): Promise<void> {
    await pending.run(`site:update:${site.id}`, async () => {
      try {
        await api(`/v1/appnotes/sites/${site.id}`, {
          method: "PATCH",
          csrf: me.csrf_token,
          body,
        });
        onChange();
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
    });
  }

  async function deleteSite(site: Site): Promise<void> {
    await pending.run(`site:delete:${site.id}`, async () => {
      try {
        await api(`/v1/appnotes/sites/${site.id}`, {
          method: "DELETE",
          csrf: me.csrf_token,
        });
        setPendingDeletion(null);
        onChange();
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
    });
  }

  async function reviewSite(site: Site): Promise<void> {
    await pending.run(`site:review:${site.id}`, async () => {
      setMessage(null);
      setLaunchingSiteId(site.id);
      const result = await launchSiteReview({
        mint: () =>
          api<{ readonly launch_url: string }>(
            `/v1/appnotes/sites/${site.id}/token`,
            {
              method: "POST",
              csrf: me.csrf_token,
              body: { url: site.primary_url },
            },
          ),
        navigate: (url) => window.location.assign(url),
      });
      if (!result.ok) setMessage(errorMessage(result.cause));
      setLaunchingSiteId(null);
    });
  }

  const deletion =
    administrator && pendingDeletion !== null ? (
      <AlertDialog
        open
        onOpenChange={(open) => {
          if (!open) setPendingDeletion(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {pendingDeletion.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes {pendingDeletion.name} and all of its
              feedback.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={pending.isPending(`site:delete:${pendingDeletion.id}`)}
              onClick={() => void deleteSite(pendingDeletion)}
              type="button"
              variant="danger"
            >
              {pending.isPending(`site:delete:${pendingDeletion.id}`)
                ? "Deleting…"
                : "Delete site"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    ) : null;

  if (view.kind === "setup") {
    const site =
      sites.find((candidate) => candidate.id === view.siteId) ??
      (createdSite?.id === view.siteId ? createdSite : null);
    if (site === null) {
      return (
        <div className="site-flow" data-step="install">
          <p className="loading-label" role="status">
            {loading ? "Loading…" : "This site is no longer available."}
          </p>
        </div>
      );
    }
    return setupStatus(site, installation.check) === "now" ? (
      <SiteConnectedView
        launching={launchingSiteId === site.id}
        onBack={backToList}
        onOpen={() => void reviewSite(site)}
        site={site}
      />
    ) : (
      <SiteInstallView
        agentPrompt={agentConnectionPrompt({
          apiOrigin,
          membership,
          mcpAvailable: me.mcp_available === true,
        })}
        apiOrigin={apiOrigin}
        controller={installation}
        site={site}
      />
    );
  }

  const firstRun = !loading && message === null && sites.length === 0;
  if (administrator && (view.kind === "add" || firstRun)) {
    return (
      <form
        className="site-flow"
        data-step="add"
        onSubmit={(event) => void createSite(event)}
      >
        <h1>{firstRun ? "Add your first site" : "Add a site"}</h1>
        <p>
          A site is the web address your team reviews. You add a small script to
          it once.
        </p>
        <label className="site-flow-label" htmlFor="site-name">
          Name
        </label>
        <input
          autoComplete="off"
          className="quiet-field"
          id="site-name"
          name="name"
          required
        />
        <label className="site-flow-label" htmlFor="site-address">
          Address
        </label>
        <input
          className="quiet-field"
          id="site-address"
          name="primary_url"
          placeholder="https://"
          required
          type="url"
        />
        {addError === null ? null : <Notice tone="danger">{addError}</Notice>}
        <div className="site-flow-actions" data-align="end">
          {firstRun ? null : (
            <button
              className="text-action-lg"
              onClick={backToList}
              type="button"
            >
              Cancel
            </button>
          )}
          <Button
            className={inkButtonLarge}
            disabled={pending.isPending("site:create")}
            type="submit"
          >
            {pending.isPending("site:create") ? "Adding…" : "Add site"}
          </Button>
        </div>
      </form>
    );
  }

  const activeSites = sites.filter((site) => site.enabled);
  const archivedSites = sites.filter((site) => !site.enabled);
  const row = (site: Site) => (
    <SiteRow
      key={site.id}
      administrator={administrator}
      launching={launchingSiteId === site.id}
      onDelete={setPendingDeletion}
      onOpen={openSite}
      onOpenAccess={onOpenAccess}
      onOpenSetup={openSetup}
      onReview={reviewSite}
      onUpdate={updateSite}
      pending={pending.isPending(`site:update:${site.id}`)}
      site={site}
    />
  );
  return (
    <Page
      actions={
        administrator ? (
          <Button
            className={inkButton}
            onClick={() => {
              setAddError(null);
              setView({ kind: "add" });
            }}
            type="button"
          >
            <PlusIcon />
            Add site
          </Button>
        ) : undefined
      }
      title="Sites"
    >
      {message !== null && <Notice tone="danger">{message}</Notice>}
      <div aria-busy={loading} className="site-list">
        {loading && sites.length === 0 ? (
          <QuietLoading label="Loading sites" />
        ) : sites.length === 0 ? (
          message === null ? (
            <QuietLine>
              A team administrator adds sites. Once one is added, it shows up
              here.
            </QuietLine>
          ) : null
        ) : (
          <>
            {activeSites.length === 0 ? (
              <QuietLine>
                {administrator
                  ? "No active sites. Add one, or restore one from Archived."
                  : "No active sites. An administrator can restore one from Archived."}
              </QuietLine>
            ) : (
              <ul aria-label="Sites" className="site-rows">
                {activeSites.map(row)}
              </ul>
            )}
            {archivedSites.length === 0 ? null : (
              <QuietHistory summary={`Archived ${archivedSites.length}`}>
                <ul aria-label="Archived sites" className="site-rows">
                  {archivedSites.map(row)}
                </ul>
              </QuietHistory>
            )}
          </>
        )}
      </div>
      {deletion}
    </Page>
  );
}

/** "https://northstar.example.com/" reads without the bare trailing slash. */
function displayUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.pathname === "/" && parsed.search === "" && parsed.hash === ""
      ? parsed.origin
      : url;
  } catch {
    return url;
  }
}

function SiteRow({
  administrator,
  launching,
  onDelete,
  onOpen,
  onOpenAccess,
  onOpenSetup,
  onReview,
  onUpdate,
  pending,
  site,
}: {
  readonly administrator: boolean;
  readonly launching: boolean;
  readonly onDelete: (site: Site) => void;
  readonly onOpen: (site: Site) => void;
  readonly onOpenAccess: (siteId: string) => void;
  readonly onOpenSetup: (site: Site) => void;
  readonly onReview: (site: Site) => Promise<void>;
  readonly onUpdate: (
    site: Site,
    body: { readonly enabled: boolean },
  ) => Promise<void>;
  readonly pending: boolean;
  readonly site: Site;
}) {
  const connected = (site.last_connected_at ?? null) !== null;
  const archived = !site.enabled;
  return (
    <li className="site-row" data-archived={archived}>
      <button
        aria-label={`${site.name}. ${connected ? `Connected, ${site.open_note_count} open notes` : "Not set up"}${archived ? ". Archived" : ""}`}
        className="site-row-open"
        onClick={() => onOpen(site)}
        title={`Last activity ${exactTime(site.last_activity_at)}`}
        type="button"
      >
        <span aria-hidden="true" className="site-tile">
          {initial(site.name)}
        </span>
        <span className="site-row-text">
          <span className="site-row-name">{site.name}</span>
          <span className="site-row-url">{displayUrl(site.primary_url)}</span>
        </span>
        <span className="site-row-status">
          {connected ? "Connected" : "Not set up"}
        </span>
        <span className="site-row-count">{site.open_note_count} open</span>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={`More actions for ${site.name}`}
          className="site-row-more"
          disabled={pending}
        >
          <MoreIcon />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuGroup>
            {archived ? null : (
              <DropdownMenuItem
                disabled={launching}
                onClick={() => void onReview(site)}
              >
                <HugeiconsIcon icon={ExternalLinkIcon} strokeWidth={1.5} />
                {launching ? "Opening…" : "Review site"}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => onOpenSetup(site)}>
              <HugeiconsIcon icon={Plug01Icon} strokeWidth={1.5} />
              Setup and status
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onOpenAccess(site.id)}>
              <HugeiconsIcon icon={LinkSquare01Icon} strokeWidth={1.5} />
              Guest access
            </DropdownMenuItem>
            {administrator ? (
              <DropdownMenuItem
                disabled={pending}
                onClick={() => void onUpdate(site, { enabled: archived })}
              >
                <HugeiconsIcon icon={Archive01Icon} strokeWidth={1.5} />
                {archived ? "Restore site" : "Archive site"}
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuGroup>
          {administrator ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem
                  onClick={() => onDelete(site)}
                  variant="destructive"
                >
                  <HugeiconsIcon icon={Delete02Icon} strokeWidth={1.5} />
                  Delete site
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}

function NotesPage({
  initialSiteId,
  me,
  membership,
  revision,
  onChange,
  sites,
}: PageProps & {
  readonly initialSiteId: string | null;
  readonly sites: readonly Site[] | null;
}) {
  const api = useScopedApi();
  const [notes, setNotes] = useState<readonly Note[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Note | null>(null);
  const noteApi = useScopedApi(selectedId ?? "no-note");
  const [filter, setFilter] = useState<NoteStateFilter>("open");
  const [siteFilter, setSiteFilter] = useState<string | null>(initialSiteId);
  const [pageFilter, setPageFilter] = useState<PageFilter | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [threadMessage, setThreadMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingNote, setEditingNote] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const pending = usePendingActions();
  const retryOperations = useRetryOperationIds();
  const responseGuardRef = useRef<OrganizationResponseGuard | null>(null);
  const noteActionsRef = useRef<HTMLButtonElement>(null);
  const returnFocusTo = useRef<string | null>(null);
  responseGuardRef.current ??= new OrganizationResponseGuard(
    membership.organization_id,
  );
  const responseGuard = responseGuardRef.current;

  async function loadNotes(): Promise<void> {
    const request = responseGuard.begin();
    setLoading(true);
    const query = new URLSearchParams({ limit: "100", state: filter });
    if (siteFilter !== null) query.set("site_id", siteFilter);
    try {
      const value = await api<{ notes: Note[] }>(
        `/v1/orgs/${membership.organization_id}/appnotes/notes?${query.toString()}`,
        { signal: request.signal },
      );
      if (!responseGuard.owns(request)) return;
      setNotes(value.notes);
      setSelected((current) =>
        current === null
          ? null
          : (value.notes.find((note) => note.id === current.id) ?? current),
      );
      setMessage(null);
    } catch (cause) {
      if (!responseGuard.owns(request)) return;
      setMessage(errorMessage(cause));
    } finally {
      if (responseGuard.owns(request)) setLoading(false);
    }
  }

  useEffect(() => {
    responseGuard.selectOrganization(membership.organization_id);
    void loadNotes();
    return () => responseGuard.invalidate();
  }, [membership.organization_id, filter, siteFilter, revision]);

  /** Re-read the open note itself, even when the list filter now hides it. */
  async function refreshSelected(note: Note): Promise<void> {
    try {
      const value = await noteApi<{ notes: Note[] }>(
        `/v1/appnotes/sites/${note.site_id}/notes?number=${note.number}`,
      );
      const fresh = value.notes[0];
      if (fresh !== undefined)
        setSelected((current) =>
          current?.id === fresh.id ? keepSiteName(fresh, current) : current,
        );
    } catch (cause) {
      if (!requestCanceled(cause)) setThreadMessage(errorMessage(cause));
    }
  }

  function openNote(note: Note): void {
    setSelectedId(note.id);
    setSelected(note);
    setEditingNote(false);
    setThreadMessage(null);
  }

  function closeNote(): void {
    returnFocusTo.current = selectedId;
    setSelectedId(null);
    setSelected(null);
    setEditingNote(false);
  }

  // Back in the list, focus returns to the row of the note just closed.
  useEffect(() => {
    const id = returnFocusTo.current;
    if (selected !== null || id === null) return;
    returnFocusTo.current = null;
    document
      .querySelector<HTMLButtonElement>(`[data-note-id="${CSS.escape(id)}"]`)
      ?.focus();
  }, [selected]);

  const canModifySelected =
    selected !== null &&
    canModifyAuthoredContent({
      role: membership.role,
      userId: me.user.id,
      authorId: selected.author,
    });

  async function updateNote(body: unknown, action: string): Promise<boolean> {
    if (selected === null) return false;
    const note = selected;
    const done = await pending.run(action, async () => {
      try {
        const updated = await noteApi<Note>(
          `/v1/appnotes/sites/${note.site_id}/notes/${note.id}`,
          {
            method: "PATCH",
            csrf: me.csrf_token,
            body,
          },
        );
        setSelected((current) =>
          current?.id === updated.id ? keepSiteName(updated, current) : current,
        );
        setThreadMessage(null);
        await loadNotes();
        onChange();
        return true;
      } catch (cause) {
        if (requestCanceled(cause)) return false;
        setThreadMessage(errorMessage(cause));
        return false;
      }
    });
    return done === true;
  }

  async function addReply(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (selected === null) return;
    const note = selected;
    const form = event.currentTarget;
    const body = String(new FormData(form).get("body") ?? "");
    const action = `reply:create:${note.id}`;
    const retryAction = `${action}:${body}`;
    await pending.run(action, async () => {
      try {
        await noteApi(
          `/v1/appnotes/sites/${note.site_id}/notes/${note.id}/replies`,
          {
            method: "POST",
            csrf: me.csrf_token,
            idempotencyKey: retryOperations.get(retryAction),
            body: { body },
          },
        );
        retryOperations.complete(retryAction);
        form.reset();
        setThreadMessage(null);
        await refreshSelected(note);
        await loadNotes();
      } catch (cause) {
        if (requestCanceled(cause)) return;
        setThreadMessage(errorMessage(cause));
      }
    });
  }

  async function editBody(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const body = String(new FormData(event.currentTarget).get("body") ?? "");
    if (selected === null) return;
    if (await updateNote({ body }, `note:edit:${selected.id}`)) {
      setEditingNote(false);
      window.requestAnimationFrame(() => noteActionsRef.current?.focus());
    }
  }

  async function deleteNote(): Promise<void> {
    if (selected === null) return;
    const note = selected;
    await pending.run(`note:delete:${note.id}`, async () => {
      try {
        await noteApi(`/v1/appnotes/sites/${note.site_id}/notes/${note.id}`, {
          method: "DELETE",
          csrf: me.csrf_token,
        });
        setConfirmingDelete(false);
        setSelectedId(null);
        setSelected(null);
        await loadNotes();
        onChange();
      } catch (cause) {
        if (requestCanceled(cause)) return;
        setConfirmingDelete(false);
        setThreadMessage(errorMessage(cause));
      }
    });
  }

  async function deleteReply(replyId: string): Promise<void> {
    if (selected === null) return;
    const note = selected;
    await pending.run(`reply:delete:${replyId}`, async () => {
      try {
        await noteApi(
          `/v1/appnotes/sites/${note.site_id}/notes/${note.id}/replies/${replyId}`,
          { method: "DELETE", csrf: me.csrf_token },
        );
        await refreshSelected(note);
        await loadNotes();
      } catch (cause) {
        if (requestCanceled(cause)) return;
        setThreadMessage(errorMessage(cause));
      }
    });
  }

  // Escape closes the open note, unless a menu, a dialog or a field has it.
  useEffect(() => {
    if (selected === null) return;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target.closest("input, textarea, select, [contenteditable='true']") !==
          null
      )
        return;
      if (
        document.querySelector(
          "[role='dialog'], [role='alertdialog'], [role='menu']",
        ) !== null
      )
        return;
      closeNote();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [selected?.id]);

  const visible = notes.filter((note) => matchesPage(note, pageFilter));
  const counts =
    sites === null || pageFilter !== null
      ? null
      : stateCounts(sites, siteFilter);
  const filtered = siteFilter !== null || pageFilter !== null;
  const listBody =
    loading && notes.length === 0 ? (
      <LoadingRows />
    ) : notes.length === 0 && message !== null ? (
      <div className="grid justify-items-start gap-3">
        <Notice tone="danger">{message}</Notice>
        <Button
          type="button"
          variant="outline"
          onClick={() => void loadNotes()}
        >
          Retry loading notes
        </Button>
      </div>
    ) : visible.length === 0 ? (
      <EmptyState
        action={
          filtered ? (
            <Button
              onClick={() => {
                setSiteFilter(null);
                setPageFilter(null);
              }}
              size="sm"
              type="button"
              variant="outline"
            >
              Clear filters
            </Button>
          ) : undefined
        }
        icon={Comment01Icon}
        title={
          filtered
            ? "No notes match these filters"
            : filter === "resolved"
              ? "No resolved notes"
              : "No open notes"
        }
        detail={
          filtered
            ? "Try another site or page, or clear the filters."
            : filter === "resolved"
              ? "Notes you resolve move here. Reopen one to reply again."
              : "Notes left on your sites show up here. Use Review site on Sites to leave one."
        }
      />
    ) : null;

  if (selected === null) {
    return (
      <Page
        actions={
          <SiteMenu
            notes={notes}
            onPage={setPageFilter}
            onSite={(siteId) => {
              setSiteFilter(siteId);
              setPageFilter(null);
            }}
            page={pageFilter}
            siteId={siteFilter}
            sites={sites ?? []}
          />
        }
        title="Notes"
        titleAside={
          <NoteStateTabs counts={counts} filter={filter} onFilter={setFilter} />
        }
        wide
      >
        {message !== null && notes.length > 0 && (
          <Notice tone="danger">{message}</Notice>
        )}
        {listBody ?? (
          <ul aria-busy={loading} aria-label="Notes" className="note-list">
            {visible.map((note) => (
              <NoteListRow
                key={note.id}
                note={note}
                onOpen={() => openNote(note)}
              />
            ))}
          </ul>
        )}
      </Page>
    );
  }

  const author = authorView(selected);
  const path = pagePath(selected.page_url);
  const viewport = screenshotViewport(selected.screenshot);
  const image = parseNoteImage(selected.screenshot);
  const stateAction = `note:state:${selected.id}`;
  const replyAction = `reply:create:${selected.id}`;
  const resolve = () =>
    void updateNote(
      { state: selected.state === "open" ? "resolved" : "open" },
      stateAction,
    );
  return (
    <div className="note-split">
      <section aria-label="Subject" className="note-subject">
        <button className="note-back" onClick={closeNote} type="button">
          <BackIcon />
          Notes
        </button>
        <div className="note-subject-body">
          <p className="note-where">
            <b>{selected.site_name ?? "Site"}</b>
            {path}
          </p>
          {image.kind === "absent" ? null : (
            <NoteSubjectImage
              author={author.person}
              image={image}
              key={membership.organization_id + ":" + selected.id}
              noteId={selected.id}
              siteId={selected.site_id}
            />
          )}
          <details className="note-details" open={image.kind === "absent"}>
            <summary>
              Details
              <ChevronDownIcon />
            </summary>
            <dl>
              <dt>Page</dt>
              <dd>
                <a href={selected.page_url} rel="noreferrer" target="_blank">
                  {path}
                </a>{" "}
                <span className="text-fg-3">
                  on {selected.site_name ?? "this site"}
                </span>
              </dd>
              {selected.anchor === undefined ? null : (
                <>
                  <dt>Element</dt>
                  <dd className="font-mono text-xs">
                    {selected.anchor.selector}
                  </dd>
                  {selected.anchor.text_excerpt.trim() === "" ? null : (
                    <>
                      <dt>Text</dt>
                      <dd>“{selected.anchor.text_excerpt.trim()}”</dd>
                    </>
                  )}
                </>
              )}
              {viewport === null ? null : (
                <>
                  <dt>Viewport</dt>
                  <dd className="tabular-nums">{viewport}</dd>
                </>
              )}
              {image.kind === "ready" ? (
                <>
                  <dt>Image</dt>
                  <dd>
                    {image.reconstructed
                      ? "Page reconstruction, captured with the note"
                      : "Selected area, captured with the note"}
                  </dd>
                </>
              ) : null}
              {author.via === null ? null : (
                <>
                  <dt>Via</dt>
                  <dd>{author.via}</dd>
                </>
              )}
            </dl>
          </details>
        </div>
      </section>
      <section
        aria-label={`Note #${selected.number}`}
        className="note-convo"
        key={selected.id}
      >
        <header className="note-convo-head">
          <a
            className="quiet-link"
            href={selected.page_url}
            rel="noreferrer"
            target="_blank"
          >
            Open page
            <ExternalIcon />
            <span className="sr-only">(opens in a new tab)</span>
          </a>
          <span className="ml-auto" />
          {canModifySelected ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                aria-label={`More actions for note ${selected.number}`}
                render={
                  <Button
                    className="note-more"
                    ref={noteActionsRef}
                    size="icon-sm"
                    type="button"
                    variant="ghost"
                  />
                }
              >
                <MoreIcon />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={() => setEditingNote(true)}>
                    <HugeiconsIcon icon={Edit02Icon} strokeWidth={1.5} />
                    Edit note
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    onClick={() => setConfirmingDelete(true)}
                    variant="destructive"
                  >
                    <HugeiconsIcon icon={Delete02Icon} strokeWidth={1.5} />
                    Delete note
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
          <Button
            className="note-resolve"
            disabled={pending.isPending(stateAction)}
            onClick={resolve}
            type="button"
          >
            {pending.isPending(stateAction) ? (
              "Updating…"
            ) : selected.state === "open" ? (
              <>
                <CheckIcon />
                Resolve
              </>
            ) : (
              "Reopen"
            )}
          </Button>
        </header>
        <div className="note-convo-body">
          {threadMessage !== null && (
            <Notice tone="danger">{threadMessage}</Notice>
          )}
          <article className="note-first">
            <div className="note-who">
              <AuthorAvatar author={author} />
              <b>{author.name}</b>
              <AuthorKind author={author} />
              <time
                dateTime={selected.created_at}
                title={exactTime(selected.created_at)}
              >
                {relativeTime(selected.created_at)}
              </time>
            </div>
            {editingNote && canModifySelected ? (
              <form
                className="thread-edit"
                onSubmit={(event) => void editBody(event)}
              >
                <label className="sr-only" htmlFor="note-body">
                  Note text
                </label>
                <Textarea
                  autoFocus
                  defaultValue={selected.body}
                  id="note-body"
                  name="body"
                  readOnly={pending.isPending(`note:edit:${selected.id}`)}
                  required
                  rows={3}
                />
                <div className="flex gap-2">
                  <Button
                    disabled={pending.isPending(`note:edit:${selected.id}`)}
                    size="sm"
                    type="submit"
                  >
                    {pending.isPending(`note:edit:${selected.id}`)
                      ? "Saving…"
                      : "Save note"}
                  </Button>
                  <Button
                    disabled={pending.isPending(`note:edit:${selected.id}`)}
                    onClick={() => {
                      setEditingNote(false);
                      window.requestAnimationFrame(() =>
                        noteActionsRef.current?.focus(),
                      );
                    }}
                    size="sm"
                    type="button"
                    variant="ghost"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <p className="note-first-text">{selected.body}</p>
            )}
          </article>
          {selected.replies.length === 0 ? null : (
            <section aria-label="Replies" className="note-replies">
              {selected.replies.map((reply) => (
                <ReplyItem
                  key={reply.id}
                  reply={reply}
                  siteId={selected.site_id}
                  noteId={selected.id}
                  csrf={me.csrf_token}
                  canModify={canModifyAuthoredContent({
                    role: membership.role,
                    userId: me.user.id,
                    authorId: reply.author,
                  })}
                  deleting={pending.isPending(`reply:delete:${reply.id}`)}
                  onSaved={async () => {
                    await refreshSelected(selected);
                    await loadNotes();
                  }}
                  onDelete={() => void deleteReply(reply.id)}
                />
              ))}
            </section>
          )}
        </div>
        {canReplyToNote(selected.state) ? (
          <form
            className="note-reply"
            key={selected.id}
            onSubmit={(event) => void addReply(event)}
          >
            <label className="sr-only" htmlFor="reply-body">
              Reply
            </label>
            <textarea
              id="reply-body"
              name="body"
              onKeyDown={(event) => {
                if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault();
                  event.currentTarget.form?.requestSubmit();
                }
              }}
              placeholder={`Reply to ${firstName(author.name)}`}
              readOnly={pending.isPending(replyAction)}
              required
              rows={1}
            />
            <button
              aria-label={
                pending.isPending(replyAction) ? "Sending reply" : "Send reply"
              }
              className="note-send"
              disabled={pending.isPending(replyAction)}
              type="submit"
            >
              <SendIcon />
            </button>
          </form>
        ) : (
          <div className="note-closed">
            <CheckIcon />
            <span>
              Resolved {relativeTime(selected.updated_at).toLowerCase()}.{" "}
              <button
                disabled={pending.isPending(stateAction)}
                onClick={resolve}
                type="button"
              >
                {pending.isPending(stateAction) ? "Reopening…" : "Reopen"}
              </button>{" "}
              to add a reply.
            </span>
          </div>
        )}
      </section>
      <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete note #{selected.number}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes note #{selected.number} and every reply
              in its thread.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={pending.isPending(`note:delete:${selected.id}`)}
              onClick={() => void deleteNote()}
              type="button"
              variant="danger"
            >
              {pending.isPending(`note:delete:${selected.id}`)
                ? "Deleting…"
                : "Delete note"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** Mutation responses may omit the Site name the list read carried. */
function keepSiteName(fresh: Note, current: Note): Note {
  return fresh.site_name !== undefined || current.site_name === undefined
    ? fresh
    : { ...fresh, site_name: current.site_name };
}

function ReplyItem({
  reply,
  siteId,
  noteId,
  csrf,
  canModify,
  deleting,
  onSaved,
  onDelete,
}: {
  readonly reply: Reply;
  readonly siteId: string;
  readonly noteId: string;
  readonly csrf: string;
  readonly canModify: boolean;
  readonly deleting: boolean;
  readonly onSaved: () => Promise<void>;
  readonly onDelete: () => void;
}) {
  const api = useScopedApi();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const actionsRef = useRef<HTMLButtonElement>(null);
  const restoreEditFocus = useRef(false);
  const pending = usePendingActions();
  const author = authorView(reply);
  useEffect(() => {
    if (editing) {
      editorRef.current?.focus();
      restoreEditFocus.current = true;
    } else if (restoreEditFocus.current) {
      actionsRef.current?.focus();
      restoreEditFocus.current = false;
    }
  }, [editing]);
  async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const body = String(new FormData(event.currentTarget).get("body") ?? "");
    await pending.run("save", async () => {
      setMessage(null);
      try {
        await api(
          `/v1/appnotes/sites/${siteId}/notes/${noteId}/replies/${reply.id}`,
          {
            method: "PATCH",
            csrf,
            body: { body },
          },
        );
        setEditing(false);
        await onSaved();
      } catch (cause) {
        if (!requestCanceled(cause)) setMessage(errorMessage(cause));
      }
    });
  }
  return (
    <article aria-label={`Reply from ${author.name}`} className="note-message">
      <AuthorAvatar author={author} />
      <div className="min-w-0">
        <div className="note-who">
          <b>{author.name}</b>
          <AuthorKind author={author} />
          <time dateTime={reply.created_at} title={exactTime(reply.created_at)}>
            {relativeTime(reply.created_at)}
          </time>
        </div>
        {message !== null && <Notice tone="danger">{message}</Notice>}
        {editing && canModify ? (
          <form className="thread-edit" onSubmit={(event) => void save(event)}>
            <label className="sr-only" htmlFor={`edit-reply-${reply.id}`}>
              Edit reply
            </label>
            <Textarea
              id={`edit-reply-${reply.id}`}
              ref={editorRef}
              name="body"
              defaultValue={reply.body}
              readOnly={pending.isPending("save")}
              required
              rows={3}
            />
            <div className="flex gap-2">
              <Button
                type="submit"
                size="sm"
                disabled={pending.isPending("save")}
              >
                {pending.isPending("save") ? "Saving…" : "Save reply"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={pending.isPending("save")}
                onClick={() => {
                  setEditing(false);
                  setMessage(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <p className="note-message-text">{reply.body}</p>
        )}
      </div>
      {canModify && !editing ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label={`Actions for the reply from ${author.name}`}
            disabled={deleting}
            render={
              <Button
                className="note-message-actions"
                ref={actionsRef}
                size="icon-sm"
                type="button"
                variant="ghost"
              />
            }
          >
            <HugeiconsIcon icon={MoreHorizontalIcon} strokeWidth={1.5} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => setEditing(true)}>
                <HugeiconsIcon icon={Edit02Icon} strokeWidth={1.5} />
                Edit reply
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => setConfirming(true)}
                variant="destructive"
              >
                <HugeiconsIcon icon={Delete02Icon} strokeWidth={1.5} />
                Delete reply
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this reply?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the reply from this note.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={onDelete}
              type="button"
              variant="danger"
            >
              {deleting ? "Deleting…" : "Delete reply"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </article>
  );
}

function AccessPage({
  focusedSiteId,
  me,
  membership,
  revision,
  onChange,
  onOpenAgentInstructions,
}: PageProps & {
  readonly focusedSiteId: string | null;
  readonly onOpenAgentInstructions: () => void;
}) {
  const api = useScopedApi();
  const [keyInventory, setKeyInventory] = useState<Inventory<ApiKey>>({
    status: "loading",
  });
  const [siteInventory, setSiteInventory] = useState<Inventory<Site>>({
    status: "loading",
  });
  const [linkInventory, setLinkInventory] = useState<Inventory<GuestLink>>({
    status: "loading",
  });
  const [accessRevision, setAccessRevision] = useState(0);
  const sites = useMemo(
    () => (siteInventory.status === "ready" ? siteInventory.items : []),
    [siteInventory],
  );
  const keys = keyInventory.status === "ready" ? keyInventory.items : [];
  const links = linkInventory.status === "ready" ? linkInventory.items : [];
  const recoveryStore = useMemo(createBrowserCredentialRecoveryStore, []);
  const [recoveries, setRecoveries] = useState<readonly CredentialRecovery[]>(
    () => recoveryStore.list(membership.organization_id, me.user.id),
  );
  const [revealedCredential, setRevealedCredential] =
    useState<RevealedCredential | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [keyDialogOpen, setKeyDialogOpen] = useState(false);
  const [keyCreationError, setKeyCreationError] = useState<string | null>(null);
  const [guestDialogOpen, setGuestDialogOpen] = useState(false);
  const [guestDialogSiteId, setGuestDialogSiteId] = useState<string | null>(
    focusedSiteId,
  );
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);
  const pending = usePendingActions();
  const retryOperations = useRetryOperationIds();
  const administrator = canAdministerWorkspace(membership.role);
  const visibleRecoveries = useMemo(
    () => credentialRecoveriesForAccess(recoveries, administrator, sites),
    [administrator, recoveries, sites],
  );
  // A guest link Copy already put on the clipboard keeps its receipt for Copy
  // but no longer asks to be saved.
  const unsavedRecoveries = visibleRecoveries.filter(
    credentialRecoveryNeedsSaving,
  );
  const enabledSites = sites.filter((site) => site.enabled);
  const guestRows = guestLinkRows({
    links,
    sites,
    recoveries: visibleRecoveries,
  });
  const keyRows = apiKeyRows(keys);

  useEffect(() => {
    setGuestDialogSiteId(focusedSiteId);
    if (focusedSiteId === null) return;
    const heading = document.getElementById("guest-links-title");
    heading?.focus({ preventScroll: true });
    heading?.scrollIntoView({ block: "nearest" });
  }, [focusedSiteId]);

  useEffect(() => {
    if (copiedLinkId === null) return;
    const timeout = window.setTimeout(() => setCopiedLinkId(null), 1_500);
    return () => window.clearTimeout(timeout);
  }, [copiedLinkId]);

  useEffect(() => {
    setSiteInventory({ status: "loading" });
    setKeyInventory({ status: "loading" });
    setLinkInventory({ status: "loading" });
    setRecoveries(recoveryStore.list(membership.organization_id, me.user.id));
    setRevealedCredential(null);
    setKeyDialogOpen(false);
    setKeyCreationError(null);
    setMessage(null);
  }, [administrator, me.user.id, membership.organization_id, recoveryStore]);
  useEffect(() => {
    const nextExpiry = Math.min(
      ...visibleRecoveries.map((recovery) =>
        Date.parse(recovery.recoverableUntil),
      ),
    );
    if (!Number.isFinite(nextExpiry)) return;
    const timeout = window.setTimeout(
      () => {
        const current = recoveryStore.list(
          membership.organization_id,
          me.user.id,
        );
        const currentVisible = credentialRecoveriesForAccess(
          current,
          administrator,
          sites,
        );
        setRecoveries(current);
        if (
          currentVisible.filter(credentialRecoveryNeedsSaving).length <
          visibleRecoveries.filter(credentialRecoveryNeedsSaving).length
        ) {
          setMessage(
            "A credential recovery window ended. Revoke any credential you did not save, then create another.",
          );
        }
      },
      Math.max(0, nextExpiry - Date.now() + 50),
    );
    return () => window.clearTimeout(timeout);
  }, [
    me.user.id,
    administrator,
    membership.organization_id,
    recoveryStore,
    sites,
    visibleRecoveries,
  ]);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    async function loadInventory<T>(
      request: () => Promise<readonly T[]>,
      setInventory: React.Dispatch<React.SetStateAction<Inventory<T>>>,
    ): Promise<void> {
      setInventory((current) =>
        current.status === "ready" ? current : { status: "loading" },
      );
      try {
        const items = await request();
        if (active) setInventory({ status: "ready", items });
      } catch (cause) {
        if (active)
          setInventory({ status: "error", message: errorMessage(cause) });
      }
    }

    const input = { signal: controller.signal };
    void loadInventory(
      async () =>
        (
          await api<{ sites: Site[] }>(
            `/v1/orgs/${membership.organization_id}/appnotes/sites?limit=100`,
            input,
          )
        ).sites,
      setSiteInventory,
    );
    void loadInventory(
      async () =>
        (
          await api<{ guest_links: GuestLink[] }>(
            `/v1/orgs/${membership.organization_id}/appnotes/guest-links`,
            input,
          )
        ).guest_links,
      setLinkInventory,
    );
    if (administrator) {
      void loadInventory(
        async () =>
          (
            await api<{ api_keys: ApiKey[] }>(
              `/v1/orgs/${membership.organization_id}/api-keys`,
              input,
            )
          ).api_keys,
        setKeyInventory,
      );
    } else {
      setKeyInventory({ status: "ready", items: [] });
    }
    return () => {
      active = false;
      controller.abort();
    };
  }, [administrator, membership.organization_id, revision, accessRevision]);

  async function createKey(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const action = "api-key:create";
    const name = String(data.get("name") ?? "");
    const agentName = String(data.get("agent_name") ?? "");
    const retryAction = `${action}:${JSON.stringify([name, agentName])}`;
    const operationId = retryOperations.get(retryAction);
    const recovery = recoveryStore.beginApiKey({
      agentName: agentName || null,
      name,
      operationId,
      organizationId: membership.organization_id,
      userId: me.user.id,
    });
    setRecoveries(recoveryStore.list(membership.organization_id, me.user.id));
    await pending.run(action, async () => {
      try {
        const result = await api<{
          plaintext: string;
          recoverable_until: string;
        }>(`/v1/orgs/${membership.organization_id}/api-keys`, {
          method: "POST",
          csrf: me.csrf_token,
          idempotencyKey: operationId,
          body: {
            name,
            agent_name: agentName || null,
          },
        });
        const current = recoveryStore.setDeadline(
          recovery,
          result.recoverable_until,
        );
        setRecoveries(
          recoveryStore.list(membership.organization_id, me.user.id),
        );
        setRevealedCredential({
          label: "Copy this API key",
          recovery: current,
          value: result.plaintext,
        });
        setKeyCreationError(null);
        setKeyDialogOpen(false);
        form.reset();
        onChange();
      } catch (cause) {
        if (credentialRecoveryCannotContinue(cause)) {
          recoveryStore.remove(recovery.id);
          retryOperations.complete(retryAction);
          setRecoveries(
            recoveryStore.list(membership.organization_id, me.user.id),
          );
        }
        setKeyCreationError(errorMessage(cause));
      }
    });
  }

  /** Issue or replay a guest link through its receipt; see issueGuestLinkFromReceipt. */
  async function issueGuestLink(
    recovery: GuestLinkCredentialRecovery,
  ): Promise<{
    readonly recovery: GuestLinkCredentialRecovery;
    readonly shareUrl: string;
  }> {
    const issued = await issueGuestLinkFromReceipt({
      csrf: me.csrf_token,
      recovery,
      request: api,
      store: recoveryStore,
    });
    setRecoveries(recoveryStore.list(membership.organization_id, me.user.id));
    return issued;
  }

  function forgetUnrecoverable(
    recovery: CredentialRecovery,
    cause: unknown,
  ): void {
    if (credentialRecoveryCannotContinue(cause)) {
      recoveryStore.remove(recovery.id);
      retryOperations.complete(credentialRetryAction(recovery));
      setRecoveries(recoveryStore.list(membership.organization_id, me.user.id));
    }
    setMessage(errorMessage(cause));
  }

  async function createGuestLink(
    site: Site,
    expiresAt: string | null,
  ): Promise<void> {
    if (!site.enabled) {
      setMessage("Restore this Site before creating guest access.");
      return;
    }
    const action = `guest-link:create:${site.id}`;
    await pending.run(action, async () => {
      const operationId = retryOperations.get(action);
      const recovery = recoveryStore.beginGuestLink({
        operationId,
        organizationId: membership.organization_id,
        siteId: site.id,
        siteName: site.name,
        userId: me.user.id,
        expiresAt,
      });
      setRecoveries(recoveryStore.list(membership.organization_id, me.user.id));
      setGuestDialogOpen(false);
      try {
        const issued = await issueGuestLink(recovery);
        setRevealedCredential({
          label: `Copy the guest link for ${site.name}`,
          recovery: issued.recovery,
          value: issued.shareUrl,
        });
        setMessage(null);
        onChange();
      } catch (cause) {
        forgetUnrecoverable(recovery, cause);
      }
    });
  }

  async function recoverCredential(
    recovery: CredentialRecovery,
  ): Promise<void> {
    if (
      recovery.kind === "guest_link" &&
      !sites.some((site) => site.id === recovery.siteId && site.enabled)
    ) {
      setMessage("Restore this Site before finishing its guest link.");
      return;
    }
    await pending.run(`credential:recover:${recovery.id}`, async () => {
      try {
        if (recovery.kind === "api_key") {
          const result = await api<{
            plaintext: string;
            recoverable_until: string;
          }>(`/v1/orgs/${recovery.organizationId}/api-keys`, {
            method: "POST",
            csrf: me.csrf_token,
            idempotencyKey: recovery.operationId,
            body: {
              name: recovery.name,
              agent_name: recovery.agentName,
            },
          });
          const current = recoveryStore.setDeadline(
            recovery,
            result.recoverable_until,
          );
          setRecoveries(
            recoveryStore.list(membership.organization_id, me.user.id),
          );
          setRevealedCredential({
            label: "Copy this API key",
            recovery: current,
            value: result.plaintext,
          });
        } else {
          const issued = await issueGuestLink(recovery);
          setRevealedCredential({
            label: `Copy the guest link for ${recovery.siteName}`,
            recovery: issued.recovery,
            value: issued.shareUrl,
          });
        }
        setMessage(null);
        onChange();
      } catch (cause) {
        forgetUnrecoverable(recovery, cause);
      }
    });
  }

  /**
   * Copy on a guest link row (see copyGuestLinkFromReceipt). A successful
   * copy marks the receipt copied: the row keeps Copy until the window ends,
   * and the queue and the end-of-window notice stop asking to save it.
   */
  async function copyGuestLink(
    linkId: string,
    recovery: GuestLinkCredentialRecovery,
  ): Promise<void> {
    await pending.run(`credential:recover:${recovery.id}`, async () => {
      const outcome = await copyGuestLinkFromReceipt({
        csrf: me.csrf_token,
        recovery,
        request: api,
        store: recoveryStore,
        writeClipboard: (text) => navigator.clipboard.writeText(text),
      });
      setRecoveries(recoveryStore.list(membership.organization_id, me.user.id));
      switch (outcome.kind) {
        case "copied":
          retryOperations.complete(credentialRetryAction(recovery));
          setCopiedLinkId(linkId);
          setMessage(null);
          return;
        case "show":
          setRevealedCredential({
            label: `Copy the guest link for ${recovery.siteName}`,
            recovery: outcome.recovery,
            value: outcome.shareUrl,
          });
          return;
        case "dropped":
          retryOperations.complete(credentialRetryAction(recovery));
          setMessage(errorMessage(outcome.cause));
          return;
        case "failed":
          setMessage(errorMessage(outcome.cause));
          return;
      }
    });
  }

  function acknowledgeCredential(recovery: CredentialRecovery): void {
    recoveryStore.remove(recovery.id);
    retryOperations.complete(credentialRetryAction(recovery));
    setRecoveries(recoveryStore.list(membership.organization_id, me.user.id));
    setRevealedCredential(null);
  }

  async function revokeKey(key: ApiKey): Promise<void> {
    await pending.run(`api-key:revoke:${key.id}`, async () => {
      try {
        await api(`/v1/orgs/${membership.organization_id}/api-keys/${key.id}`, {
          method: "DELETE",
          csrf: me.csrf_token,
        });
        onChange();
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
    });
  }

  async function revokeGuestLink(link: GuestLink): Promise<void> {
    await pending.run(`guest-link:revoke:${link.id}`, async () => {
      try {
        await api(`/v1/appnotes/sites/${link.site_id}/guest-links/${link.id}`, {
          method: "DELETE",
          csrf: me.csrf_token,
        });
        onChange();
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
    });
  }

  function openGuestDialog(): void {
    setGuestDialogSiteId((current) =>
      current !== null && enabledSites.some((site) => site.id === current)
        ? current
        : (enabledSites[0]?.id ?? null),
    );
    setGuestDialogOpen(true);
  }

  const guestDialogSite =
    enabledSites.find((site) => site.id === guestDialogSiteId) ?? null;
  const unfinishedLink =
    guestDialogSite === null
      ? undefined
      : unsavedRecoveries.find(
          (recovery): recovery is GuestLinkCredentialRecovery =>
            recovery.kind === "guest_link" &&
            recovery.siteId === guestDialogSite.id,
        );
  const guestCreating =
    guestDialogSite !== null &&
    pending.isPending(`guest-link:create:${guestDialogSite.id}`);
  const retryInventory = (label: string) => (
    <button
      className="text-action"
      onClick={() => setAccessRevision((value) => value + 1)}
      type="button"
    >
      {label}
    </button>
  );

  const guestRow = (row: GuestLinkRow) => {
    const recoverAction =
      row.recovery === null ? null : `credential:recover:${row.recovery.id}`;
    const copying = recoverAction !== null && pending.isPending(recoverAction);
    return (
      <QuietRow
        actions={
          row.state === "active" || row.state === "paused" ? (
            <>
              {row.recovery === null ? null : (
                <button
                  aria-label={`Copy the guest link for ${row.siteName}`}
                  className="text-action"
                  disabled={copying}
                  onClick={() => {
                    if (row.recovery !== null)
                      void copyGuestLink(row.link.id, row.recovery);
                  }}
                  type="button"
                >
                  {copying
                    ? "Copying…"
                    : copiedLinkId === row.link.id
                      ? "Copied"
                      : "Copy"}
                </button>
              )}
              <ConfirmButton
                description="This revokes the guest link and every active session created from it."
                label="Revoke"
                onConfirm={() => void revokeGuestLink(row.link)}
                pending={pending.isPending(`guest-link:revoke:${row.link.id}`)}
                quiet
                title={`Revoke this guest link for ${row.siteName}?`}
              />
            </>
          ) : undefined
        }
        detail={row.detail}
        dimmed={row.state !== "active"}
        key={row.link.id}
        name={row.siteName}
        title={`Created ${exactTime(row.link.created_at)}`}
      />
    );
  };

  return (
    <Page maxWidth={920} title="Access">
      {message !== null && <Notice tone="danger">{message}</Notice>}
      {unsavedRecoveries.length > 0 && (
        <CredentialRecoveryQueue
          pending={pending}
          recoveries={unsavedRecoveries}
          onRecover={(recovery) => void recoverCredential(recovery)}
        />
      )}
      <QuietSection
        action={
          enabledSites.length > 0 ? (
            <button
              className="text-action-strong"
              onClick={openGuestDialog}
              type="button"
            >
              <PlusIcon />
              New link
            </button>
          ) : undefined
        }
        description="Let someone without an account review one site."
        title="Guest links"
        titleId="guest-links-title"
      >
        {linkInventory.status === "error" ? (
          <QuietLine action={retryInventory("Retry")} tone="danger">
            Guest links could not be loaded. {linkInventory.message}
          </QuietLine>
        ) : siteInventory.status === "error" ? (
          <QuietLine action={retryInventory("Retry")} tone="danger">
            Sites could not be loaded. {siteInventory.message}
          </QuietLine>
        ) : linkInventory.status === "loading" ||
          siteInventory.status === "loading" ? (
          <QuietLoading label="Loading guest links" />
        ) : sites.length === 0 ? (
          <QuietLine>
            {administrator
              ? "Add a site on Sites before creating guest links."
              : "A team administrator adds sites. Guest links follow once one exists."}
          </QuietLine>
        ) : guestRows.current.length === 0 ? (
          <QuietLine>No guest links yet.</QuietLine>
        ) : (
          <QuietRows label="Guest links">
            {guestRows.current.map(guestRow)}
          </QuietRows>
        )}
        {guestRows.history.length === 0 ? null : (
          <QuietHistory summary={guestLinkHistorySummary(guestRows.history)}>
            <QuietRows label="Guest link history">
              {guestRows.history.map(guestRow)}
            </QuietRows>
          </QuietHistory>
        )}
        <span className="sr-only" role="status">
          {copiedLinkId === null ? "" : "Guest link copied"}
        </span>
      </QuietSection>
      <QuietSection
        action={
          administrator ? (
            <button
              className="text-action-strong"
              onClick={() => {
                setKeyCreationError(null);
                setKeyDialogOpen(true);
              }}
              type="button"
            >
              <PlusIcon />
              New key
            </button>
          ) : undefined
        }
        description="For scripts and agents. Owners and admins only."
        title="API keys"
        titleId="api-keys-title"
      >
        {!administrator ? (
          <QuietLine>Only team administrators can manage API keys.</QuietLine>
        ) : keyInventory.status === "error" ? (
          <QuietLine action={retryInventory("Retry")} tone="danger">
            API keys could not be loaded. {keyInventory.message}
          </QuietLine>
        ) : keyInventory.status === "loading" ? (
          <QuietLoading label="Loading API keys" />
        ) : keyRows.current.length === 0 ? (
          <QuietLine
            action={
              <button
                className="text-action-strong"
                onClick={onOpenAgentInstructions}
                type="button"
              >
                Agent instructions
              </button>
            }
          >
            No API keys yet. Scripts and agents that cannot sign in through a
            browser use one.
          </QuietLine>
        ) : (
          <QuietRows label="API keys">
            {keyRows.current.map((key) => (
              <QuietRow
                actions={
                  <ConfirmButton
                    description={`Agents using ${key.name} will immediately lose access.`}
                    label="Revoke"
                    onConfirm={() => void revokeKey(key)}
                    pending={pending.isPending(`api-key:revoke:${key.id}`)}
                    quiet
                    title={`Revoke ${key.name}?`}
                  />
                }
                detail={
                  <span
                    title={
                      key.last_used_at === null
                        ? undefined
                        : exactTime(key.last_used_at)
                    }
                  >
                    {usedLabel(key.last_used_at)}
                  </span>
                }
                key={key.id}
                name={
                  <>
                    {key.name}
                    {key.agent_name === null ? null : (
                      <small>{key.agent_name}</small>
                    )}
                  </>
                }
                title={`Created ${exactTime(key.created_at)}`}
              />
            ))}
          </QuietRows>
        )}
        {administrator && keyRows.revoked.length > 0 ? (
          <QuietHistory summary={`${keyRows.revoked.length} revoked`}>
            <QuietRows label="Revoked API keys">
              {keyRows.revoked.map((key) => (
                <QuietRow
                  detail={sinceLabel(
                    "Revoked",
                    key.revoked_at ?? key.created_at,
                  )}
                  dimmed
                  key={key.id}
                  name={
                    <>
                      {key.name}
                      {key.agent_name === null ? null : (
                        <small>{key.agent_name}</small>
                      )}
                    </>
                  }
                />
              ))}
            </QuietRows>
          </QuietHistory>
        ) : null}
      </QuietSection>
      {me.mcp_available === true && (
        <ConnectedAgents
          key={me.user.id}
          me={me}
          membership={membership}
          onOpenAgentInstructions={onOpenAgentInstructions}
        />
      )}
      <Dialog
        open={guestDialogOpen}
        onOpenChange={(open) => setGuestDialogOpen(open)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New guest link</DialogTitle>
            <DialogDescription>
              Anyone with this link can join the site review. Choose the site
              and when the link stops accepting guests.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (guestDialogSite === null) return;
              if (unfinishedLink !== undefined) {
                setGuestDialogOpen(false);
                void recoverCredential(unfinishedLink);
                return;
              }
              const duration = new FormData(event.currentTarget).get("expiry");
              const days = duration === "7" ? 7 : duration === "30" ? 30 : null;
              const expiresAt =
                days === null
                  ? null
                  : new Date(Date.now() + days * 86_400_000).toISOString();
              void createGuestLink(guestDialogSite, expiresAt);
            }}
          >
            <FieldGroup>
              <FieldRoot>
                <FieldLabel htmlFor="guest-link-site">Site</FieldLabel>
                <NativeSelect
                  className="w-full"
                  id="guest-link-site"
                  onChange={(event) => setGuestDialogSiteId(event.target.value)}
                  value={guestDialogSite?.id ?? ""}
                >
                  {enabledSites.map((site) => (
                    <NativeSelectOption key={site.id} value={site.id}>
                      {site.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </FieldRoot>
              <FieldRoot>
                <FieldLabel htmlFor="guest-link-expiry">
                  Link expires
                </FieldLabel>
                <NativeSelect
                  className="w-full"
                  defaultValue="never"
                  disabled={unfinishedLink !== undefined}
                  id="guest-link-expiry"
                  name="expiry"
                >
                  <NativeSelectOption value="never">
                    Never (until revoked)
                  </NativeSelectOption>
                  <NativeSelectOption value="7">In 7 days</NativeSelectOption>
                  <NativeSelectOption value="30">In 30 days</NativeSelectOption>
                </NativeSelect>
                {unfinishedLink === undefined ? null : (
                  <FieldDescription>
                    A link for this site was not saved yet. Finish saving it
                    before you create another.
                  </FieldDescription>
                )}
              </FieldRoot>
              <DialogFooter>
                <Button
                  className={inkButton}
                  disabled={guestDialogSite === null || guestCreating}
                  type="submit"
                >
                  {guestCreating
                    ? "Creating…"
                    : unfinishedLink === undefined
                      ? "Create link"
                      : "Finish link"}
                </Button>
              </DialogFooter>
            </FieldGroup>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog
        open={keyDialogOpen}
        onOpenChange={(open) => {
          setKeyDialogOpen(open);
          if (open) setKeyCreationError(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New API key</DialogTitle>
            <DialogDescription>
              Create a credential for an agent or integration. The plaintext key
              is shown once.
            </DialogDescription>
          </DialogHeader>
          {keyCreationError !== null && (
            <Notice tone="danger">{keyCreationError}</Notice>
          )}
          <form onSubmit={(event) => void createKey(event)}>
            <FieldGroup>
              <Field
                label="Key name"
                name="name"
                placeholder="Design QA agent"
                required
              />
              <Field
                label="Agent name (optional)"
                name="agent_name"
                placeholder="Codex"
              />
              <DialogFooter>
                <Button
                  className={inkButton}
                  disabled={pending.isPending("api-key:create")}
                  type="submit"
                >
                  {pending.isPending("api-key:create")
                    ? "Creating…"
                    : "Create key"}
                </Button>
              </DialogFooter>
            </FieldGroup>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog
        open={revealedCredential !== null}
        onOpenChange={(open) => {
          if (!open) setRevealedCredential(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save this credential</DialogTitle>
            <DialogDescription>
              AppNotes does not store the plaintext. If you close this dialog,
              you can reopen it from Access until the recovery window ends.
            </DialogDescription>
          </DialogHeader>
          {revealedCredential !== null && (
            <>
              <SecretReveal
                key={revealedCredential.recovery.operationId}
                label={revealedCredential.label}
                value={revealedCredential.value}
              />
              <DialogFooter>
                <Button
                  className={inkButton}
                  type="button"
                  onClick={() =>
                    acknowledgeCredential(revealedCredential.recovery)
                  }
                >
                  I saved it
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
}

function BillingPage({ me, membership }: Pick<PageProps, "me" | "membership">) {
  const api = useScopedApi();
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<"checkout" | "portal" | "resync" | null>(
    null,
  );
  const [message, setMessage] = useState<string | null>(null);
  const administrator = membership.role !== "member";
  const responseGuardRef = useRef<OrganizationResponseGuard | null>(null);
  responseGuardRef.current ??= new OrganizationResponseGuard(
    membership.organization_id,
  );
  const responseGuard = responseGuardRef.current;

  async function load(): Promise<void> {
    const request = responseGuard.begin();
    try {
      const nextSummary = await api<BillingSummary>(
        `/v1/orgs/${membership.organization_id}/billing`,
        { signal: request.signal },
      );
      if (!responseGuard.owns(request)) return;
      setSummary(nextSummary);
      setMessage(null);
    } catch (cause) {
      if (!responseGuard.owns(request)) return;
      setMessage(errorMessage(cause));
    } finally {
      if (responseGuard.owns(request)) setLoading(false);
    }
  }

  useEffect(() => {
    responseGuard.selectOrganization(membership.organization_id);
    setSummary(null);
    setMessage(null);
    setBusy(null);
    setLoading(true);
    void load();
    return () => responseGuard.invalidate();
  }, [membership.organization_id]);

  async function redirectToBilling(
    action: "checkout" | "portal",
  ): Promise<void> {
    setBusy(action);
    try {
      const result = await api<{ readonly url: string }>(
        `/v1/orgs/${membership.organization_id}/billing/${action}`,
        { method: "POST", csrf: me.csrf_token },
      );
      window.location.assign(result.url);
    } catch (cause) {
      setMessage(errorMessage(cause));
      setBusy(null);
    }
  }

  async function resync(): Promise<void> {
    setBusy("resync");
    try {
      setSummary(
        await api<BillingSummary>(
          `/v1/orgs/${membership.organization_id}/billing/resync`,
          { method: "POST", csrf: me.csrf_token },
        ),
      );
      setMessage("Billing status refreshed.");
    } catch (cause) {
      setMessage(errorMessage(cause));
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return (
      <Page title="Billing">
        <LoadingRows />
      </Page>
    );
  }
  if (summary === null) {
    return (
      <Page title="Billing">
        <StatePanel
          action={<Button onClick={() => void load()}>Try again</Button>}
          description={message ?? "Billing status could not be loaded."}
          title="Billing unavailable"
        />
      </Page>
    );
  }

  const team = summary.offer_id === "appnotes_team_v1";
  const cap = summary.active_site_cap;
  const capacityUnits =
    team && cap !== null ? Math.max(1, Math.ceil(cap / 25)) : 0;
  const monthlyPrice = 79 * capacityUnits;
  const usagePercent =
    cap === null || cap === 0
      ? 0
      : Math.min(100, (summary.active_sites_this_month / cap) * 100);
  const periodLabel = summary.cancel_at_period_end ? "Access ends" : "Renews";
  const failedEvents = summary.failed_events ?? [];

  return (
    <Page title="Billing">
      {message !== null ? <Notice>{message}</Notice> : null}
      {summary.billing_state === "grace" ? (
        <Alert variant="destructive">
          <AlertTitle>Payment needs attention</AlertTitle>
          <AlertDescription>
            Team remains active through{" "}
            {calendarDate(summary.grace_ends_at ?? "")}. Update the payment
            method before then to avoid falling back to Free.
          </AlertDescription>
        </Alert>
      ) : null}
      {summary.has_conflict ? (
        <Alert variant="destructive">
          <AlertTitle>Billing needs review</AlertTitle>
          <AlertDescription>
            More than one AppNotes subscription was found. Access is preserved,
            but Checkout is blocked until support resolves the duplicate.
          </AlertDescription>
        </Alert>
      ) : null}
      {failedEvents.length > 0 ? (
        <Alert variant="destructive">
          <AlertTitle>Billing update needs attention</AlertTitle>
          <AlertDescription>
            {failedEvents.length === 1
              ? "One Stripe update could not be reconciled."
              : failedEvents.length +
                " Stripe updates could not be reconciled."}{" "}
            Your current AppNotes access is unchanged. Refresh the status to
            repair the local projection.
            <details className="billing-failed-events">
              <summary>View failed updates</summary>
              <ul>
                {failedEvents.map((event) => (
                  <li key={event.id}>
                    <strong>{event.kind}</strong>
                    <span>{calendarDate(event.received_at)}</span>
                  </li>
                ))}
              </ul>
            </details>
          </AlertDescription>
        </Alert>
      ) : null}
      <ConsoleSection
        actions={
          <StatusBadge tone={team ? "primary" : "neutral"}>
            Current plan
          </StatusBadge>
        }
        title={team ? "Team" : "Free"}
        titleId="billing-plan-title"
      >
        <div className="billing-overview">
          <div className="billing-price">
            <strong>{team ? `$${monthlyPrice}` : "$0"}</strong>
            <span>
              {team
                ? `${capacityUnits} × 25-Site capacity ${capacityUnits === 1 ? "block" : "blocks"}, billed monthly`
                : "free forever"}
            </span>
          </div>
          <div className="billing-usage">
            <div className="billing-usage-heading">
              <strong>Active Sites this month</strong>
              <span>
                {summary.active_sites_this_month} of {cap ?? "unlimited"}
              </span>
            </div>
            <div
              aria-label={`${summary.active_sites_this_month} of ${cap ?? "unlimited"} active Sites used`}
              aria-valuemax={cap ?? undefined}
              aria-valuemin={0}
              aria-valuenow={summary.active_sites_this_month}
              className="billing-meter"
              role="progressbar"
            >
              <span style={{ width: `${usagePercent}%` }} />
            </div>
            <p>
              A Site counts only after it receives an annotation during the
              current UTC month. Registered and archived Sites are unlimited.
            </p>
          </div>
          <dl className="billing-facts">
            <div>
              <dt>People</dt>
              <dd>{team ? "Unlimited members" : "One owner"}</dd>
            </div>
            <div>
              <dt>Guests and agents</dt>
              <dd>Unlimited</dd>
            </div>
            {team ? (
              <div>
                <dt>Additional capacity</dt>
                <dd>$79 for each additional 25 active Sites</dd>
              </div>
            ) : null}
            {team && summary.renews_at !== null ? (
              <div>
                <dt>{periodLabel}</dt>
                <dd>{calendarDate(summary.renews_at)}</dd>
              </div>
            ) : null}
          </dl>
          {administrator ? (
            <div className="billing-actions">
              {summary.can_upgrade ? (
                <Button
                  disabled={busy !== null}
                  onClick={() => void redirectToBilling("checkout")}
                  type="button"
                >
                  {busy === "checkout"
                    ? "Opening Checkout…"
                    : "Upgrade to Team"}
                </Button>
              ) : summary.can_manage_billing ? (
                <Button
                  disabled={busy !== null}
                  onClick={() => void redirectToBilling("portal")}
                  type="button"
                >
                  {busy === "portal"
                    ? "Opening portal…"
                    : "Manage plan and capacity"}
                </Button>
              ) : null}
              <Button
                disabled={busy !== null}
                onClick={() => void resync()}
                type="button"
                variant="outline"
              >
                {busy === "resync" ? "Refreshing…" : "Refresh status"}
              </Button>
            </div>
          ) : null}
        </div>
      </ConsoleSection>
    </Page>
  );
}

function TeamPage({ me, membership, onChange }: Omit<PageProps, "revision">) {
  const api = useScopedApi();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<readonly TeamMember[]>([]);
  const [management, setManagement] = useState<{
    readonly organizationId: string;
    readonly mode: MembershipManagement;
  } | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<TeamMember | null>(null);
  const [invitations, setInvitations] = useState<InvitationInventory>({
    status: "loading",
  });
  const [resendOffer, setResendOffer] = useState<PendingInvitation | null>(
    null,
  );
  const [inviteRole, setInviteRole] = useState<"admin" | "member">("member");
  const pending = usePendingActions();
  const responseGuardRef = useRef<OrganizationResponseGuard | null>(null);
  responseGuardRef.current ??= new OrganizationResponseGuard(
    membership.organization_id,
  );
  const responseGuard = responseGuardRef.current;
  const invitationGuardRef = useRef<OrganizationResponseGuard | null>(null);
  invitationGuardRef.current ??= new OrganizationResponseGuard(
    membership.organization_id,
  );
  const invitationGuard = invitationGuardRef.current;
  const managementMode =
    management?.organizationId === membership.organization_id
      ? management.mode
      : null;
  const canAdministerMembers = canManageMembers(
    membership.role,
    managementMode,
  );

  async function load(): Promise<void> {
    const request = responseGuard.begin();
    setLoading(true);
    try {
      const result = await api<{
        members: TeamMember[];
        membership_management: MembershipManagement;
      }>(`/v1/orgs/${membership.organization_id}/members`, {
        signal: request.signal,
      });
      if (!responseGuard.owns(request)) return;
      setMembers(result.members);
      setManagement({
        organizationId: membership.organization_id,
        mode: result.membership_management,
      });
      setMessage(null);
    } catch (cause) {
      if (!responseGuard.owns(request)) return;
      setMessage(errorMessage(cause));
    } finally {
      if (responseGuard.owns(request)) setLoading(false);
    }
  }
  useEffect(() => {
    responseGuard.selectOrganization(membership.organization_id);
    setMembers([]);
    setManagement(null);
    setMessage(null);
    setResendOffer(null);
    void load();
    return () => responseGuard.invalidate();
  }, [membership.organization_id]);

  /**
   * Re-read pending invitations. The skeleton shows only on the first read;
   * later reads keep the current rows until the fresh list arrives.
   */
  async function loadInvitations(
    sent?: PendingInvitation,
  ): Promise<readonly PendingInvitation[] | null> {
    const request = invitationGuard.begin();
    try {
      const fresh = withSentInvitation(
        await readPendingInvitations(
          api,
          membership.organization_id,
          request.signal,
        ),
        sent,
      );
      if (!invitationGuard.owns(request)) return null;
      setInvitations({ status: "ready", invitations: fresh });
      return fresh;
    } catch (cause) {
      if (!invitationGuard.owns(request) || requestCanceled(cause)) return null;
      // A failed re-read keeps the rows already on screen.
      setInvitations((current) =>
        current.status === "ready"
          ? current
          : { status: "error", message: errorMessage(cause) },
      );
      return null;
    }
  }
  useEffect(() => {
    invitationGuard.selectOrganization(membership.organization_id);
    setInvitations({ status: "loading" });
    if (canAdministerMembers) void loadInvitations();
    return () => invitationGuard.invalidate();
  }, [membership.organization_id, canAdministerMembers]);

  async function invite(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const email = String(data.get("email") ?? "").trim();
    await pending.run("member:invite", async () => {
      setResendOffer(null);
      try {
        const result = await api<{
          invited: true;
          invitation?: PendingInvitation;
        }>(invitationsPath(membership.organization_id), {
          method: "POST",
          csrf: me.csrf_token,
          body: { email, role: data.get("role") },
        });
        form.reset();
        setInviteRole("member");
        setMessage(`Invitation sent to ${email}.`);
        const sent = result.invitation;
        if (sent !== undefined) {
          setInvitations((current) => ({
            status: "ready",
            invitations: withSentInvitation(
              current.status === "ready" ? current.invitations : [],
              sent,
            ),
          }));
        }
        await loadInvitations(sent);
      } catch (cause) {
        const refusal = inviteRefusal(cause);
        if (refusal.kind === "already_member") {
          setMessage(`${email} is already a member of this team.`);
          return;
        }
        if (refusal.kind === "other") {
          setMessage(refusal.message);
          return;
        }
        setMessage(`${email} already has a pending invitation.`);
        const fresh = await loadInvitations();
        setResendOffer(
          fresh === null ? null : (pendingInvitationFor(fresh, email) ?? null),
        );
      }
    });
  }

  async function resendInvitation(
    invitation: PendingInvitation,
  ): Promise<void> {
    await pending.run(`invitation:resend:${invitation.id}`, async () => {
      setResendOffer(null);
      try {
        await api(
          `${invitationPath(membership.organization_id, invitation.id)}/resend`,
          { method: "POST", csrf: me.csrf_token },
        );
        setMessage(`Invitation sent again to ${invitation.email}.`);
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
      await loadInvitations();
    });
  }

  async function revokeInvitation(
    invitation: PendingInvitation,
  ): Promise<void> {
    await pending.run(`invitation:revoke:${invitation.id}`, async () => {
      setResendOffer(null);
      try {
        await api(invitationPath(membership.organization_id, invitation.id), {
          method: "DELETE",
          csrf: me.csrf_token,
        });
        setMessage(`Invitation to ${invitation.email} revoked.`);
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
      await loadInvitations();
    });
  }

  async function changeRole(
    userId: string,
    role: "admin" | "member",
  ): Promise<void> {
    await pending.run(`member:role:${userId}`, async () => {
      try {
        await api(`/v1/orgs/${membership.organization_id}/members/${userId}`, {
          method: "PATCH",
          csrf: me.csrf_token,
          body: { role },
        });
        await load();
        onChange();
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
    });
  }

  async function resync(): Promise<void> {
    await pending.run("identity:resync", async () => {
      try {
        await api(`/v1/orgs/${membership.organization_id}/identity/resync`, {
          method: "POST",
          csrf: me.csrf_token,
        });
        await load();
        setMessage("Directory synchronized.");
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
    });
  }

  async function removeMember(member: TeamMember): Promise<void> {
    await pending.run(`member:remove:${member.user_id}`, async () => {
      try {
        await api(
          `/v1/orgs/${membership.organization_id}/members/${member.user_id}`,
          { method: "DELETE", csrf: me.csrf_token },
        );
        await load();
        onChange();
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
    });
  }

  const roleLabel = (role: TeamMember["role"]) =>
    role === "owner" ? "Owner" : role === "admin" ? "Admin" : "Member";
  const roleRank = (role: TeamMember["role"]) =>
    role === "owner" ? 0 : role === "admin" ? 1 : 2;
  const orderedMembers = [...members].sort(
    (left, right) => roleRank(left.role) - roleRank(right.role),
  );

  return (
    <Page
      actions={
        canAdministerMembers ? (
          <button
            className="text-action-strong"
            disabled={pending.isPending("identity:resync")}
            onClick={() => void resync()}
            title="Refresh members and roles from the configured identity provider"
            type="button"
          >
            {pending.isPending("identity:resync")
              ? "Syncing…"
              : "Sync directory"}
          </button>
        ) : undefined
      }
      maxWidth={820}
      title="Team"
    >
      {message !== null && (
        <Notice>
          {message}
          {resendOffer === null ? null : (
            <>
              {" "}
              <Button
                disabled={pending.isPending(
                  `invitation:resend:${resendOffer.id}`,
                )}
                onClick={() => void resendInvitation(resendOffer)}
                size="sm"
                type="button"
                variant="link"
              >
                Resend it
              </Button>
            </>
          )}
        </Notice>
      )}
      {canAdministerMembers && (
        <form className="invite-row" onSubmit={(event) => void invite(event)}>
          <div className="invite-field">
            <label className="sr-only" htmlFor="invite-email">
              Email
            </label>
            <input
              autoComplete="off"
              id="invite-email"
              name="email"
              placeholder="name@company.com"
              required
              type="email"
            />
            <input name="role" type="hidden" value={inviteRole} />
            <DropdownMenu>
              <DropdownMenuTrigger
                aria-label={`Invite as ${roleLabel(inviteRole)}. Change role`}
                className="invite-role"
              >
                {roleLabel(inviteRole)}
                <ChevronDownIcon />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[160px]">
                <DropdownMenuGroup>
                  <DropdownMenuLabel>Invite as</DropdownMenuLabel>
                  <DropdownMenuRadioGroup
                    value={inviteRole}
                    onValueChange={(value) => {
                      if (value === "admin" || value === "member")
                        setInviteRole(value);
                    }}
                  >
                    <DropdownMenuRadioItem closeOnClick value="member">
                      Member
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem closeOnClick value="admin">
                      Admin
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Button
            className={`${inkButtonLarge} h-[38px]`}
            disabled={pending.isPending("member:invite")}
            type="submit"
          >
            {pending.isPending("member:invite") ? "Sending…" : "Invite"}
          </Button>
        </form>
      )}
      <QuietSection className="team-members" titleId="team-members-title">
        {managementMode === "identity_provider" ? (
          <p className="quiet-note">
            Members and roles come from your login provider. Change them there.
          </p>
        ) : null}
        {loading && members.length === 0 ? (
          <QuietLoading label="Loading members" />
        ) : managementMode === null ? (
          <QuietLine
            action={
              <button
                className="text-action"
                onClick={() => void load()}
                type="button"
              >
                Retry
              </button>
            }
            tone="danger"
          >
            Members could not be loaded.
          </QuietLine>
        ) : members.length === 0 ? (
          <QuietLine>
            Members appear here once they sign in to this team.
          </QuietLine>
        ) : (
          <ul aria-busy={loading} aria-label="Members" className="member-rows">
            {orderedMembers.map((member) => {
              const manageable = canAdministerMember({
                actorRole: membership.role,
                actorUserId: me.user.id,
                management: managementMode,
                memberRole: member.role,
                memberUserId: member.user_id,
              });
              const otherRole = member.role === "admin" ? "member" : "admin";
              const busy =
                pending.isPending(`member:role:${member.user_id}`) ||
                pending.isPending(`member:remove:${member.user_id}`);
              return (
                <li className="member-row" key={member.user_id}>
                  <AvatarForUser
                    id={member.user_id}
                    name={member.display_name}
                    size="default"
                  />
                  <span className="member-row-text">
                    <span className="member-row-name">
                      {member.display_name}
                      {member.user_id === me.user.id ? (
                        <span className="sr-only"> (you)</span>
                      ) : null}
                    </span>
                    {member.email === null ||
                    member.email === undefined ? null : (
                      <span className="member-row-email">{member.email}</span>
                    )}
                  </span>
                  {manageable ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        aria-label={`${roleLabel(member.role)}. Change role for ${member.display_name}`}
                        className="member-role"
                        data-manageable
                        disabled={busy}
                      >
                        {busy ? "Updating…" : roleLabel(member.role)}
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[200px]">
                        <DropdownMenuGroup>
                          <DropdownMenuItem
                            onClick={() =>
                              void changeRole(member.user_id, otherRole)
                            }
                          >
                            Make {roleLabel(otherRole).toLowerCase()}
                          </DropdownMenuItem>
                        </DropdownMenuGroup>
                        <DropdownMenuSeparator />
                        <DropdownMenuGroup>
                          <DropdownMenuItem
                            onClick={() => setPendingRemoval(member)}
                            variant="destructive"
                          >
                            <HugeiconsIcon
                              icon={Delete02Icon}
                              strokeWidth={1.5}
                            />
                            Remove from team
                          </DropdownMenuItem>
                        </DropdownMenuGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <span className="member-role">
                      {roleLabel(member.role)}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </QuietSection>
      {canAdministerMembers && (
        <PendingInvitationsSection
          inventory={invitations}
          isPending={pending.isPending}
          onResend={(invitation) => void resendInvitation(invitation)}
          onRetry={() => {
            setInvitations({ status: "loading" });
            void loadInvitations();
          }}
          onRevoke={(invitation) => void revokeInvitation(invitation)}
        />
      )}
      <AlertDialog
        open={pendingRemoval !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemoval(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove {pendingRemoval?.display_name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRemoval?.display_name} will lose access to this team and
              its AppNotes content.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={
                pendingRemoval !== null &&
                pending.isPending(`member:remove:${pendingRemoval.user_id}`)
              }
              onClick={() => {
                if (pendingRemoval !== null) void removeMember(pendingRemoval);
              }}
              type="button"
              variant="danger"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}

interface PageProps {
  readonly me: Me;
  readonly membership: Membership;
  readonly revision: number;
  readonly onChange: () => void;
}

function OrganizationGate({
  me,
  onChange,
  onSessionError,
}: {
  readonly me: Me;
  readonly onChange: (me: Me | null) => void;
  readonly onSessionError: (message: string) => void;
}) {
  const api = useScopedApi();
  const [message, setMessage] = useState<string | null>(null);
  const pending = usePendingActions();
  async function select(organizationId: string): Promise<void> {
    await pending.run("organization:select", async () => {
      setMessage(null);
      const result = await selectOrganizationSession(
        api,
        organizationId,
        me.csrf_token,
      );
      switch (result.status) {
        case "confirmed":
          onChange(result.me);
          setMessage(result.notice);
          break;
        case "signed_out":
          onChange(null);
          break;
        case "unavailable":
          onSessionError(result.message);
          break;
        case "cancelled":
          break;
      }
    });
  }
  async function create(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const name = new FormData(event.currentTarget).get("name");
    await pending.run("organization:create", async () => {
      try {
        const result = await api<{ organization_id: string }>("/v1/orgs", {
          method: "POST",
          csrf: me.csrf_token,
          body: { name },
        });
        await select(result.organization_id);
      } catch (cause) {
        setMessage(errorMessage(cause));
      }
    });
  }
  return (
    <div className="center-screen">
      <Brand />
      <section className="gate-card">
        <p className="eyebrow">Welcome, {me.user.display_name}</p>
        <h1>Choose your team</h1>
        <p>AppNotes keeps sites, feedback, and access scoped to one team.</p>
        {message !== null && <Notice>{message}</Notice>}
        <div className="organization-list">
          {me.memberships.map((membership) => (
            <button
              className="organization-button"
              disabled={pending.isPending("organization:select")}
              key={membership.organization_id}
              type="button"
              onClick={() => void select(membership.organization_id)}
            >
              <strong>{membership.organization_name}</strong>
              <span>{membership.role}</span>
            </button>
          ))}
        </div>
        <form onSubmit={(event) => void create(event)}>
          <FieldGroup className="mt-8 gap-5 border-t pt-6">
            <Field
              label="Or create a team"
              name="name"
              placeholder="Acme product"
              required
            />
            <Button
              disabled={pending.isPending("organization:create")}
              type="submit"
            >
              {pending.isPending("organization:create")
                ? "Creating…"
                : "Create team"}
            </Button>
          </FieldGroup>
        </form>
        {me.mcp_available === true && (
          <div className="mt-8">
            <ConnectedAgents key={me.user.id} me={me} />
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * The page frame: 44px from the top, 64px in, at most 1000px wide (1080px
 * for the Notes list), a 20px title, optional text beside it and one action
 * on the right.
 */
function Page({
  actions,
  maxWidth,
  title,
  titleAside,
  wide = false,
  children,
}: {
  readonly actions?: React.ReactNode;
  /** The mock's narrower pages: Access is 920px, Team 820px. */
  readonly maxWidth?: number;
  readonly title: string;
  readonly titleAside?: React.ReactNode;
  readonly wide?: boolean;
  readonly children: React.ReactNode;
}) {
  return (
    <div
      className="page"
      data-wide={wide}
      style={maxWidth === undefined ? undefined : { maxWidth }}
    >
      <PageHeader actions={actions} aside={titleAside} title={title} />
      {children}
    </div>
  );
}
function NavButton({
  active,
  count,
  icon: Icon,
  onClick,
  children,
}: {
  readonly active: boolean;
  readonly count?: number | null;
  readonly icon: (props: React.SVGProps<SVGSVGElement>) => React.ReactNode;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}) {
  return (
    <button
      className="workspace-nav-button"
      type="button"
      aria-current={active ? "page" : undefined}
      onClick={onClick}
    >
      <Icon />
      {children}
      {count === undefined || count === null || count === 0 ? null : (
        <span className="workspace-nav-count">
          {count}
          <span className="sr-only"> open</span>
        </span>
      )}
    </button>
  );
}
/** The team's square: ink for the current team, its slot colour otherwise. */
function TeamMonogram({
  current,
  membership,
}: {
  readonly current: boolean;
  readonly membership: Membership;
}) {
  return (
    <span
      aria-hidden="true"
      className="organization-monogram"
      data-current={current}
      style={
        current
          ? undefined
          : ({
              "--slot": `var(--p${slotOf(membership.organization_id)})`,
            } as React.CSSProperties)
      }
    >
      {initial(membership.organization_name)}
    </span>
  );
}
function TeamSwitcher({
  current,
  disabled,
  memberships,
  onSelect,
  switching,
}: {
  readonly current: Membership;
  readonly disabled: boolean;
  readonly memberships: readonly Membership[];
  readonly onSelect: (organizationId: string) => void;
  readonly switching: boolean;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Current team: ${current.organization_name}. Switch team`}
        className="organization-switcher"
        disabled={disabled}
      >
        <TeamMonogram current membership={current} />
        <span className="organization-switcher-name">
          {switching ? "Switching team…" : current.organization_name}
        </span>
        <UpDownIcon />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[280px]">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Teams</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={current.organization_id}
            onValueChange={(value) => {
              if (
                typeof value === "string" &&
                value !== current.organization_id
              )
                onSelect(value);
            }}
          >
            {memberships.map((item) => {
              const selected = item.organization_id === current.organization_id;
              return (
                <DropdownMenuRadioItem
                  className="pr-2"
                  closeOnClick
                  key={item.organization_id}
                  value={item.organization_id}
                >
                  <TeamMonogram current={selected} membership={item} />
                  <span className="min-w-0 flex-1 truncate">
                    {item.organization_name}
                  </span>
                  {selected ? null : (
                    <span className="ml-auto shrink-0 text-xs text-fg-3 capitalize">
                      {item.role}
                    </span>
                  )}
                </DropdownMenuRadioItem>
              );
            })}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
/** The account menu, shared by the sidebar row and the narrow-screen button. */
function AccountMenuContent({
  busy,
  dark,
  membership,
  name,
  onAgentSetup,
  onLogout,
  onThemeChange,
}: {
  readonly busy: boolean;
  readonly dark: boolean;
  readonly membership: Membership;
  readonly name: string;
  readonly onAgentSetup: (kind: AgentSetupDialogKind) => void;
  readonly onLogout: () => void;
  readonly onThemeChange: () => void;
}) {
  return (
    <DropdownMenuContent align="start" className="w-[240px]">
      <DropdownMenuGroup>
        <DropdownMenuLabel>
          <span className="account-menu-name">{name}</span>
          <span className="account-menu-role">{membership.role}</span>
        </DropdownMenuLabel>
        <DropdownMenuItem onClick={onThemeChange}>
          <HugeiconsIcon
            icon={dark ? Sun03Icon : Moon02Icon}
            strokeWidth={1.5}
          />
          Use {dark ? "light" : "dark"} theme
        </DropdownMenuItem>
        <DropdownMenuItem
          render={
            <a href={documentationUrl} rel="noreferrer" target="_blank" />
          }
        >
          <HugeiconsIcon icon={Book02Icon} strokeWidth={1.5} />
          Documentation
          <span className="sr-only">(opens in a new tab)</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onAgentSetup("instructions")}>
          <HugeiconsIcon icon={ComputerTerminal01Icon} strokeWidth={1.5} />
          Agent instructions
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onAgentSetup("install")}>
          <HugeiconsIcon icon={PackageAddIcon} strokeWidth={1.5} />
          Install skill
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuItem disabled={busy} onClick={onLogout}>
          <HugeiconsIcon icon={Logout01Icon} strokeWidth={1.5} />
          {busy ? "Please wait…" : "Sign out"}
        </DropdownMenuItem>
      </DropdownMenuGroup>
    </DropdownMenuContent>
  );
}
/** A member's disc: two initials in their slot colour, never a photo. */
function AvatarForUser({
  id,
  name,
  size,
}: {
  readonly id: string;
  readonly name: string;
  readonly size?: "sm" | "default";
}) {
  return <Avatar person={{ kind: "member", id, name }} size={size ?? "sm"} />;
}
function Field({
  label,
  name,
  type = "text",
  placeholder,
  required = false,
}: {
  readonly label: string;
  readonly name: string;
  readonly type?: string;
  readonly placeholder?: string;
  readonly required?: boolean;
}) {
  const id = `field-${name}`;
  return (
    <FieldRoot>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        name={name}
        type={type}
        placeholder={placeholder}
        required={required}
      />
    </FieldRoot>
  );
}
function Notice({
  children,
  tone = "neutral",
}: {
  readonly children: React.ReactNode;
  readonly tone?: "danger" | "neutral";
}) {
  return (
    <Alert
      role={tone === "danger" ? "alert" : "status"}
      variant={tone === "danger" ? "destructive" : "default"}
    >
      {tone === "danger" ? (
        <HugeiconsIcon icon={Alert02Icon} strokeWidth={1.5} />
      ) : null}
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}
function EmptyState({
  action,
  className,
  detail,
  icon,
  title,
}: {
  readonly action?: React.ReactNode;
  readonly className?: string;
  readonly detail: string;
  readonly icon?: IconSvgElement;
  readonly title: string;
}) {
  return (
    <Empty className={className}>
      {icon === undefined ? null : (
        <EmptyMedia variant="icon">
          <HugeiconsIcon icon={icon} strokeWidth={1.5} />
        </EmptyMedia>
      )}
      <EmptyHeader>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{detail}</EmptyDescription>
      </EmptyHeader>
      {action === undefined ? null : <EmptyContent>{action}</EmptyContent>}
    </Empty>
  );
}

function LoadingRows() {
  return (
    <div
      className="grid divide-y divide-line border-y border-line"
      aria-label="Loading"
    >
      {[0, 1, 2].map((row) => (
        <div
          className="flex h-[60px] flex-col justify-center gap-2 px-3"
          key={row}
        >
          <Skeleton className="h-3.5 w-48" />
          <Skeleton className="h-3 w-72 max-w-full" />
        </div>
      ))}
    </div>
  );
}
function SecretReveal({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}) {
  return <SecretPanel label={label} value={value} />;
}
function CredentialRecoveryQueue({
  onRecover,
  pending,
  recoveries,
}: {
  readonly onRecover: (recovery: CredentialRecovery) => void;
  readonly pending: PendingActions;
  readonly recoveries: readonly CredentialRecovery[];
}) {
  return (
    <section
      aria-labelledby="credential-recovery-title"
      className="credential-recovery-queue"
    >
      <header>
        <div>
          <h2 id="credential-recovery-title">Finish saving credentials</h2>
          <p>
            AppNotes kept a non-secret recovery receipt. Finish copying these
            values before their recovery windows close.
          </p>
        </div>
        <StatusBadge tone="primary">{recoveries.length}</StatusBadge>
      </header>
      <ul>
        {recoveries.map((recovery) => {
          const action = `credential:recover:${recovery.id}`;
          const recovering = pending.isPending(action);
          return (
            <li key={recovery.id}>
              <div>
                <strong>
                  {recovery.kind === "api_key"
                    ? recovery.name
                    : `Guest link for ${recovery.siteName}`}
                </strong>
                <span>
                  {recovery.kind === "api_key" ? "API key" : "Guest link"}
                  {" · Recovery available until "}
                  <time dateTime={recovery.recoverableUntil}>
                    {credentialRecoveryTime(recovery.recoverableUntil)}
                  </time>
                </span>
              </div>
              <Button
                disabled={recovering}
                onClick={() => onRecover(recovery)}
                size="sm"
                type="button"
              >
                {recovering ? "Recovering…" : "Finish saving"}
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function credentialRecoveriesForAccess(
  recoveries: readonly CredentialRecovery[],
  administrator: boolean,
  sites: readonly Site[],
): readonly CredentialRecovery[] {
  return recoveries.filter((recovery) => {
    if (recovery.kind === "api_key") return administrator;
    return sites.some((site) => site.id === recovery.siteId && site.enabled);
  });
}

function credentialRecoveryTime(recoverableUntil: string): string {
  return new Date(recoverableUntil).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

function credentialRetryAction(recovery: CredentialRecovery): string {
  return recovery.kind === "api_key"
    ? `api-key:create:${JSON.stringify([recovery.name, recovery.agentName ?? ""])}`
    : `guest-link:create:${recovery.siteId}`;
}

function ConfirmButton({
  description,
  label,
  onConfirm,
  pending = false,
  quiet = false,
  title,
  variant = "destructive",
}: {
  readonly description: string;
  readonly label: string;
  readonly onConfirm: () => void;
  readonly pending?: boolean;
  /** A quiet text action trigger, as on the Access and Team rows. */
  readonly quiet?: boolean;
  readonly title: string;
  readonly variant?: "destructive" | "ghost";
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger
        disabled={pending}
        render={
          quiet ? (
            <button className="text-action" type="button" />
          ) : (
            <Button size="sm" type="button" variant={variant} />
          )
        }
      >
        {pending ? `${label}…` : label}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            onClick={onConfirm}
            type="button"
            variant="danger"
          >
            {pending ? `${label}…` : label}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
function LoadingScreen() {
  return (
    <div className="center-screen">
      <Brand />
      <p className="loading-label">Loading your workspace…</p>
    </div>
  );
}
function FatalScreen({
  message,
  retry,
}: {
  readonly message: string;
  readonly retry: () => void;
}) {
  return (
    <div className="center-screen">
      <Brand />
      <section className="gate-card">
        <h1>AppNotes could not load</h1>
        <p>{message}</p>
        <Button className="mt-6" type="button" onClick={retry}>
          Try again
        </Button>
      </section>
    </div>
  );
}
function SignInScreen() {
  const localLogout =
    new URLSearchParams(window.location.search).get("logout") === "local";
  return (
    <div className="signin-screen">
      <header>
        <Brand />
        <a
          className={buttonVariants({ variant: "outline" })}
          href="/auth/login"
        >
          Sign in
        </a>
      </header>
      <main>
        {localLogout && (
          <Alert className="mb-6 max-w-xl">
            <AlertTitle>Signed out of AppNotes</AlertTitle>
            <AlertDescription>
              Your identity provider may still be signed in. Sign out there too
              if you are using a shared device.
            </AlertDescription>
          </Alert>
        )}
        <p className="eyebrow">Feedback on the live interface</p>
        <h1>Keep the conversation attached to the product.</h1>
        <p>
          AppNotes gives product teams, clients, and agents one precise place to
          review what is actually on screen.
        </p>
        <a className={buttonVariants()} href="/auth/login?return_to=%2F">
          Sign in to AppNotes
        </a>
      </main>
    </div>
  );
}

function errorMessage(cause: unknown): string {
  return cause instanceof Error
    ? cause.message
    : "The request could not be completed.";
}

function requestCanceled(cause: unknown): boolean {
  return cause instanceof ApiError && cause.code === "request_cancelled";
}
function calendarDate(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Unknown date";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(
    date,
  );
}

const rootElement = document.getElementById("root");
if (rootElement === null) {
  throw new Error("AppNotes could not find its root element.");
}

const root = createRoot(rootElement);
// Consume outside React so StrictMode cannot lose or exchange the link.
// Opening a replacement invitation in this tab can be a fragment-only visit.
let guestEntry: GuestEntry | null = null;
let guestVisit = 0;
function renderEntry() {
  guestEntry?.clear();
  guestEntry = GuestEntry.fromUrl(new URL(window.location.href));
  if (guestEntry !== null) {
    window.history.replaceState(null, "", window.location.pathname);
    guestVisit += 1;
  }
  root.render(
    <StrictMode>
      {guestEntry === null ? (
        <App />
      ) : (
        <GuestEntryScreen key={guestVisit} entry={guestEntry} />
      )}
    </StrictMode>,
  );
}
function startEntry() {
  renderEntry();
  if (guestEntry !== null) window.addEventListener("hashchange", renderEntry);
}

// Staging only: the dashboard reviews itself with AppNotes. A Review launch
// carries its credential in the fragment, so render (which can rewrite the
// URL on the guest route) only after the SDK has read it.
const selfReview = createSelfReview(window);
const launched = hasLaunchFragment(window.location.hash);
const firstReview = selfReview.start();
if (launched) void firstReview.then(startEntry);
else startEntry();
window.addEventListener("hashchange", () => {
  if (hasLaunchFragment(window.location.hash)) void selfReview.start();
});
